import type { Hono } from "hono";
import type { ComponentType } from "react";
import type { z } from "zod";
import type { AuditWriteAction, ChannelRole, ChannelStreamState, ChannelVariableOperation, ChatOutputTarget } from "../contracts/values";
import type { DryRunEntryReport } from "../contracts/dry-run";
import type { TemplateContext, TemplateFields, TemplateVariable } from "../template";
import type { SettingsEditorDefinition } from "../dashboard/ui";
import { prepareChannelModuleSettingsWrite } from "./repository/channel-module-storage";
import type { ModerationResult, ModerationTimeoutExpectation } from "./contracts/moderation";
import { templateTextLength } from "../contracts/template-text-length";
import { channelVariableTemplateToken } from "../contracts/channel-variable-template";
export type { PanelTemplateWarning } from "../panel-contract";
export type { ChatOutputTarget } from "../contracts/values";

/** The default channel timezone used by host template values and channel settings. */
export const DEFAULT_CHANNEL_TIME_ZONE = "Europe/Berlin";

/** How long overlay stream details stay cached before live viewer counts refresh. */
export const OVERLAY_STREAM_DETAILS_CACHE_TTL_MS = 60_000;

/** Maximum decoded size accepted for JSON responses from external providers. */
export const PROVIDER_JSON_MAX_BYTES = 256 * 1024;

/** A blank chat template uses the same opt-out behavior across announcement editors. */
export const hasChatMessage = (text: string): boolean => text.trim().length > 0;

/** Reads a provider JSON response while enforcing a decoded body size limit. */
export const readBoundedJsonResponseWithBytes = async (
  response: Response,
  maximumBytes = PROVIDER_JSON_MAX_BYTES,
): Promise<{ value: unknown; bytes: number }> => {
  if (!Number.isSafeInteger(maximumBytes) || maximumBytes < 1) {
    throw new RangeError("The JSON response size limit must be a positive safe integer.");
  }
  const contentLength = response.headers.get("Content-Length");
  const declaredLength = contentLength === null ? Number.NaN : Number(contentLength);
  if (Number.isFinite(declaredLength) && declaredLength > maximumBytes) {
    try { await response.body?.cancel(); } catch { /* Ignore cancellation errors after rejecting the response. */ }
    throw new Error("JSON response exceeded the size limit.");
  }
  const body = response.body;
  if (body === null) throw new Error("JSON response had no body.");
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let byteLength = 0;
  let done = false;
  try {
    while (!done) {
      const chunk = await reader.read();
      if (chunk.done) {
        done = true;
      } else {
        byteLength += chunk.value.byteLength;
        if (byteLength > maximumBytes) {
          throw new Error("JSON response exceeded the size limit.");
        }
        chunks.push(chunk.value);
      }
    }
  } catch (error) {
    try { await reader.cancel(); } catch { /* Ignore cancellation errors while releasing the reader. */ }
    throw error;
  } finally {
    reader.releaseLock();
  }

  const bytes = new Uint8Array(byteLength);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
  return { value: payload, bytes: byteLength };
};

export const readBoundedJsonResponse = async <Value = unknown>(
  response: Response,
  maximumBytes = PROVIDER_JSON_MAX_BYTES,
): Promise<Value> => (await readBoundedJsonResponseWithBytes(response, maximumBytes)).value as Value;

export const validChannelTimeZone = (timeZone: string): boolean => {
  try {
    new Intl.DateTimeFormat("en", { timeZone }).format();
    return true;
  } catch {
    return false;
  }
};

// Intl.DateTimeFormat construction is far costlier than formatToParts on an existing
// instance, and the binary searches below call these dozens of times per lookup --
// formatters are immutable and safe to reuse, so cache one per time zone.
const dateKeyFormatters = new Map<string, Intl.DateTimeFormat>();

const dateKeyFormatterFor = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = dateKeyFormatters.get(timeZone);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
    dateKeyFormatters.set(timeZone, formatter);
  }
  return formatter;
};

const localDateKeyInTimeZone = (instant: number, timeZone: string): string => {
  const parts = dateKeyFormatterFor(timeZone).formatToParts(instant);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${String(values.year)}-${String(values.month)}-${String(values.day)}`;
};

/**
 * Returns the first instant (ISO string) whose local calendar date in `timeZone` is
 * `localDate`. Solves for the date boundary directly (binary search) instead of the
 * wall-clock time "00:00", which a DST change can skip (spring-forward at midnight,
 * e.g. Africa/Cairo) or repeat (fall-back at midnight) -- a fixed-point search on that
 * wall-clock time then resolves to the wrong day. Shared by every module that needs a
 * location- or channel-local midnight, so the fix lives in one place.
 */
export const localMidnightInTimeZone = (localDate: string, timeZone: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(localDate);
  if (match === null) throw new RangeError("Invalid local calendar date.");
  const [, yearText, monthText, dayText] = match;
  const naiveUtcMidnight = Date.UTC(Number(yearText), Number(monthText) - 1, Number(dayText));
  // Real-world UTC offsets stay within -12:00..+14:00; this margin brackets the date
  // boundary on both sides regardless of offset or DST at the boundary.
  let low = naiveUtcMidnight - 26 * 60 * 60_000;
  let high = naiveUtcMidnight + 26 * 60 * 60_000;
  while (high - low > 1) {
    const mid = low + Math.floor((high - low) / 2);
    if (localDateKeyInTimeZone(mid, timeZone) < localDate) low = mid; else high = mid;
  }
  return new Date(high).toISOString();
};

/** Returns the next local midnight strictly after `from`, as a UTC epoch instant. */
export const nextLocalMidnightInTimeZone = (from: number, timeZone: string): number => {
  const today = localDateKeyInTimeZone(from, timeZone);
  let low = from;
  let high = from + 26 * 60 * 60_000;
  for (let guard = 0; localDateKeyInTimeZone(high, timeZone) === today && guard < 3; guard += 1) high += 24 * 60 * 60_000;
  while (high - low > 1) {
    const mid = low + Math.floor((high - low) / 2);
    if (localDateKeyInTimeZone(mid, timeZone) === today) low = mid; else high = mid;
  }
  return high;
};

const wallClockFormatters = new Map<string, Intl.DateTimeFormat>();

const wallClockFormatterFor = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = wallClockFormatters.get(timeZone);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    wallClockFormatters.set(timeZone, formatter);
  }
  return formatter;
};

/** The local wall-clock reading at `instant` in `timeZone`, expressed as if it were UTC. */
const localWallClockMillis = (instant: number, timeZone: string): number => {
  const parts = wallClockFormatterFor(timeZone).formatToParts(instant);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day), Number(values.hour), Number(values.minute), Number(values.second));
};

/**
 * Returns the UTC instant(s) at which the local wall-clock time in `timeZone` reads
 * `time` ("HH:MM") on `localDate`. Normally exactly one instant. A DST transition can
 * make that wall time occur twice (clocks set back, e.g. Europe/Berlin 2026-10-25
 * 02:30) -- both instants are returned, earliest first. A transition can also skip the
 * wall time entirely (clocks set forward, e.g. Africa/Cairo 2026-04-24 00:30) -- in that
 * case the single returned instant is the transition itself, since that is the instant
 * the boundary the wall time was meant to mark actually takes effect.
 */
export const wallTimeInstantsInTimeZone = (localDate: string, time: string, timeZone: string): readonly number[] => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(localDate);
  const timeMatch = /^(\d{2}):(\d{2})$/u.exec(time);
  if (dateMatch === null || timeMatch === null) throw new RangeError("Invalid local date or time.");
  const [, yearText, monthText, dayText] = dateMatch;
  const [, hourText, minuteText] = timeMatch;
  const target = Date.UTC(Number(yearText), Number(monthText) - 1, Number(dayText), Number(hourText), Number(minuteText));
  // Real-world UTC offsets stay within -12:00..+14:00 and a single DST shift is far
  // narrower than a day, so this margin brackets any transition around `target`.
  const lowBound = target - 26 * 60 * 60_000;
  const highBound = target + 26 * 60 * 60_000;
  // Intl only resolves to whole seconds, so probe on second boundaries: sampling at an
  // arbitrary millisecond would leak that sub-second remainder into the offset (it
  // reads as the previous whole second's wall clock minus the exact instant), turning
  // this offset into a false ramp instead of the step function DST actually produces.
  const offsetAt = (instant: number): number => {
    const flooredSecond = Math.floor(instant / 1000) * 1000;
    return localWallClockMillis(flooredSecond, timeZone) - flooredSecond;
  };
  const offsetLow = offsetAt(lowBound);
  const offsetHigh = offsetAt(highBound);
  if (offsetLow === offsetHigh) return [target - offsetLow];
  let low = lowBound;
  let high = highBound;
  while (high - low > 1) {
    const mid = low + Math.floor((high - low) / 2);
    if (offsetAt(mid) === offsetLow) low = mid; else high = mid;
  }
  const transition = high;
  const candidateBefore = target - offsetLow;
  const candidateAfter = target - offsetHigh;
  const instants: number[] = [];
  if (candidateBefore < transition) instants.push(candidateBefore);
  if (candidateAfter >= transition) instants.push(candidateAfter);
  return instants.length > 0 ? instants.sort((left, right) => left - right) : [transition];
};

type JsonValue = string | number | boolean | null | readonly JsonValue[] | { readonly [key: string]: JsonValue };
export type JsonObject = Readonly<Record<string, JsonValue>>;

export interface ModuleOverlayElementProps {
  elementId?: string;
  config: Readonly<Record<string, unknown>>;
  state: Readonly<Record<string, unknown>> | null;
  now: number;
  language?: "de" | "en";
}

export interface ModuleOverlayTemplateValue {
  available: boolean;
  /** UTC instant used by browser-rendered countdown values. */
  targetAt?: string;
  /** Ordered countdown instants may start with a past instant to represent an ongoing interval as zero. */
  targetAts?: readonly string[];
  /** UTC instant when this resolved value may change and its overlay should be refreshed. */
  nextChangeAt?: string;
}

export interface ModuleTemplateConditionTransition {
  at: string;
  values: Readonly<Record<string, string>>;
}

export interface ModuleQuizOverlaySnapshot {
  sequence: number;
  lastBroadcastSequence: number;
  pendingBroadcastSequence?: number | null;
  includeDisabledModule?: boolean;
  payload: Readonly<Record<string, unknown>>;
}

interface ModuleQuizOverlaySnapshotAccess {
  read: () => Promise<ModuleQuizOverlaySnapshot | null>;
  write: (
    payload: Readonly<Record<string, unknown>>,
    expectedCurrentSessionId?: string,
  ) => Promise<ModuleQuizOverlaySnapshot | null>;
  deliverPending: () => Promise<void>;
}

export interface ModuleOverlaySnapshotContext {
  kind: "snapshot";
  now: number;
  /** Reads the latest persisted module overlay state for this channel. */
  readSnapshot: () => Promise<JsonObject | null>;
}

export interface ModuleOverlayElementContext {
  kind?: "template";
  now: number;
  language: ModuleLanguage;
  channelTimeZone: () => Promise<string>;
  streamState: () => Promise<ModuleStreamState>;
  /** Latest stream start used while rendering uptime, if the lookup supplied one. */
  streamStartedAt: () => string | null;
  /** Expiration of the stream-details cache consulted during this bootstrap. */
  streamDetailsCacheExpiresAt: () => number | null;
  /** Whether a host or module lookup failed during this bootstrap. */
  hasLookupFailure: () => boolean;
  channelGameId: () => Promise<string | null>;
  renderTemplate: (text: string, mode?: ModuleTemplateRenderMode) => Promise<{ text: string; diagnostics: readonly ModuleDiagnostic[]; attributions?: readonly string[] }>;
  resolveTemplateConditions: (ids: readonly string[]) => Promise<ModuleOverlayTemplateConditions>;
  resolveTemplateConditionTransitions: (
    ids: readonly string[],
    from: number,
    until: number,
  ) => Promise<readonly ModuleTemplateConditionTransition[]>;
  resolveOverlayTemplateValues: (names: readonly string[]) => Promise<Readonly<Record<string, ModuleOverlayTemplateValue>>>;
  timeDependentTemplateConditionIds: ReadonlySet<string>;
  dynamicTemplateVariableNames: ReadonlySet<string>;
  overlayTemplateVariableNames: ReadonlySet<string>;
  /** Reads only this overlay element's module ballot in the current channel. */
  readBallot: (ballotId: string) => Promise<BallotSnapshot | null>;
}

export type ModuleOverlayInitialStateContext = ModuleOverlayElementContext | ModuleOverlaySnapshotContext;

export interface ModuleOverlayTemplateConditions {
  values: Readonly<Record<string, string>>;
  attributionsByCondition: Readonly<Record<string, readonly string[]>>;
  /** UTC instant per condition id when its resolved value may change; drives the overlay refresh schedule. */
  nextChangeAt?: Readonly<Record<string, string>>;
}

export interface OverlayElementEditorProps {
  config: JsonObject;
  onChange: (config: JsonObject) => void;
  channelId?: string;
  onPreviewState?: (state: JsonObject | null) => void;
  language?: "de" | "en";
  readOnly?: boolean;
  readOnlyReason?: string;
}

export interface ModuleOverlayElementDefinition {
  kind: `${string}.${string}`;
  configVersion: number;
  defaultSize: { width: number; height: number };
  defaultConfig: JsonObject;
  /** Editor-only sample state used when no live module state is available. */
  previewState?: (config: JsonObject, language: ModuleLanguage, now: number) => JsonObject;
  editorLabel?: Readonly<Record<ModuleLanguage, string>>;
  editorAddLabel?: Readonly<Record<ModuleLanguage, string>>;
  /** Localized one-line description shown in the overlay element palette. */
  editorDescription?: Readonly<Record<ModuleLanguage, string>>;
  editorModuleLabel?: Readonly<Record<ModuleLanguage, string>>;
  /** Omits this element from the add palette while its module is disabled. */
  hideWhenModuleDisabled?: boolean;
  parseConfig: (raw: unknown) => JsonObject | null;
  /** The module initial state uses host template, condition, or channel context. */
  initialStateNeedsContext?: boolean;
  /** The module initial state needs only its persisted snapshot and bootstrap time. */
  initialStateNeedsSnapshotContext?: boolean;
  /** Module realtime message types that require the host to reload this element's state. */
  reloadStateOnModuleMessages?: readonly string[];
  /** Module realtime message types merged directly into this element's current state. */
  mergeRealtimeStateOnModuleMessages?: readonly string[];
  /** Merges partial realtime state into the current state without replacing module lifecycle data. */
  mergeRealtimeState?: (
    current: Readonly<Record<string, unknown>> | null,
    incoming: Readonly<Record<string, unknown>>,
  ) => Readonly<Record<string, unknown>>;
  /** Keeps transient realtime state during bootstrap reloads while the same enabled element remains. */
  preserveRealtimeStateOnReload?: boolean;
  /** Host state changes that cause a generic module state message for this element. */
  reloadStateOnHostEvents?: readonly ModuleOverlayHostEvent[];
  initialState?: (
    db: D1Database,
    channelId: string,
    config: JsonObject,
    context?: ModuleOverlayElementContext,
  ) => Promise<JsonObject | null>;
  initialStateFromSnapshot?: (
    db: D1Database,
    channelId: string,
    config: JsonObject,
    context: ModuleOverlaySnapshotContext,
  ) => Promise<JsonObject | null>;
  load: () => Promise<{ default: ComponentType<ModuleOverlayElementProps> }>;
  editor?: () => Promise<{ default: ComponentType<OverlayElementEditorProps> }>;
}

export type ModuleOverlayHostEvent = "channel.game.changed" | "stream.state.changed" | "template.data.changed";

export { truncateTo200Chars, textFingerprintIfTruncated } from "../text";
export {
  effectiveTemplateVariables,
  invalidTemplateParameters,
  renderTemplate,
  templateVariableNames,
  templateWarnings,
  unknownTemplateVariables,
  worstCaseTemplateLength,
} from "../template";
export type { TemplateFields, TemplateVariable, TemplateValues } from "../template";
export { SYSTEM_TEMPLATE_VARIABLE_LIST } from "../template-variables";

/** Status of the chat-triggering person, derived from Twitch badges. */
export type ModuleChatStatus = "viewer" | "subscriber" | "vip" | "moderator" | "broadcaster";

/** Parses the deliberately narrow chat syntax shared by ballot-using modules. */
export const ballotChoiceFromMessage = (text: string, optionCount: number): number | null => {
  if (!Number.isInteger(optionCount) || optionCount < 1 || optionCount > 9) return null;
  if (!/^[1-9]$/u.test(text)) return null;
  const choice = Number(text);
  return choice <= optionCount ? choice : null;
};

/**
 * A justification for something a module did, or deliberately did not do.
 *
 * `code` is machine-readable and stable (`shoutout.suppressed`), `detail`
 * carries the numbers that explain the case. Together they answer the
 * question that today goes unanswered everywhere: "Raid detected, why was
 * there no shoutout?"
 */
/**
 * The keys a diagnostic detail may carry. Closed on purpose: the detail lands in
 * `event_log.detail_json` and the interface reads it back, so it is wire data.
 * While this was `Record<string, ...>`, German keys survived four renaming
 * passes here -- each one found by accident, because a scanner only sees the
 * shapes it was taught, and every new way of building the object slipped past
 * it. A union is seen by the compiler at every construction site, however the
 * object is assembled.
 *
 * Adding a key means adding it here, and whoever adds it sees its neighbours.
 */
type ModuleDiagnosticDetailKey =
  | "action" | "allowed" | "arguments" | "art" | "cause" | "count" | "current"
  | "currentTier" | "missing"
  | "duration" | "endsAt" | "gifter" | "kind" | "lastAdBreakAt" | "message"
  | "messageId" | "moderator" | "moduleId" | "name" | "outcome" | "person" | "alias"
  | "reason" | "recipient" | "remainingSeconds" | "requiredTier" | "response"
  | "scheduledAt" | "scheduledFor" | "scope" | "seconds" | "source"
  | "sourceChannelId" | "startedAt" | "status" | "streamState" | "target" | "targetChannelId" | "toLogin" | "variable"
  | "text" | "threshold" | "tier" | "triggerLogin" | "twitchMessage" | "type" | "viewers";

export interface ModuleDiagnostic {
  code: string;
  detail?: Readonly<Partial<Record<
    ModuleDiagnosticDetailKey,
    string | number | boolean | null | readonly ModuleChatStatus[]
  >>>;
}

/**
 * Undoes the `message` -> `twitchMessage` rename a producer's result
 * carries for the event-log diagnostic's sake (provenance: only genuinely
 * Twitch-sourced wording gets labelled "Twitch: ..." in the dashboard
 * popover, see `dashboard/events/model.ts`'s `eventCause`) -- for a route's
 * own JSON error response, which has always used `message` (including a
 * bare `null` for a timeout/network error, where there was never a Twitch
 * body to read one from -- `helixRequest` in `worker/twitch/helix.ts`) and
 * has to keep doing so for existing clients. Converts whenever the
 * `twitchMessage` key is present at all, not only when its value is a
 * nonempty string: a `null` still has to become `detail.message: null`,
 * not vanish and leave the key missing entirely, which previously read to
 * a client as "no diagnostic detail was even attempted" instead of "Twitch
 * gave no message". A producer's `result.detail` otherwise flows straight
 * into both the diagnostic and the response body; this is the one
 * conversion point every route that forwards `result.detail` as an error
 * response calls, instead of each route inlining its own rename.
 */
export const apiErrorDetail = (
  detail: Readonly<Record<string, string | number | boolean | null>>,
): Readonly<Record<string, string | number | boolean | null>> => {
  if (!Object.hasOwn(detail, "twitchMessage") || Object.hasOwn(detail, "message")) return detail;
  // `Object.hasOwn` above already proves the key exists -- `noUncheckedIndexedAccess`
  // still types a plain index-signature read as possibly `undefined`, so this narrows
  // back to what it actually is: one of the detail value types, `undefined` excluded.
  const twitchMessage = detail.twitchMessage as string | number | boolean | null;
  const rest: Record<string, string | number | boolean | null> = { ...detail };
  rest.message = twitchMessage;
  delete rest.twitchMessage;
  return rest;
};

/** A semantically well-named module action for the host to execute. */
export type ModuleChatDelivery = "sent" | "rejected" | "ambiguous" | "not_attempted";

/** Whether a definite failed or unattempted chat delivery should release an action claim. */
export const chatDeliveryReleasesClaim = (delivery: ModuleChatDelivery): boolean =>
  delivery === "rejected" || delivery === "not_attempted";

export type ModuleAction =
  | {
    kind: "chat";
    text: string;
    target?: ChatOutputTarget;
    replyToMessageId?: string;
    /** Chat outputs are automated by default; direct command responses set this false. */
    automated?: boolean;
    /** Host delivery proof for a welcome note appended to this action. */
    pointWelcome?: { claimId: string; operationId: string; text: string };
    /** Lets a module finalize a claim after the host knows the delivery outcome. */
    onDelivery?: (
      delivery: ModuleChatDelivery,
      postedWelcomeClaimId?: string,
      postedWelcomeOperationId?: string,
    ) => Promise<void>;
  }
  | { kind: "clip"; cooldownSeconds: number; postClipLinkInChat: boolean; chatLinkTemplate: string }
  | { kind: "announcement"; text: string; target?: ChatOutputTarget; automated?: boolean }
  | { kind: "shoutout"; targetChannelId: string }
  | { kind: "shoutout"; targetChannelId: string; targetLogin: string }
  | { kind: "shoutout"; targetLogin: string }
  | {
    kind: "timeout";
    userId: string;
    durationSeconds: number;
    reason: string;
    onSuccess?: Extract<ModuleAction, { kind: "chat" | "announcement" }>;
    onFailure?: Extract<ModuleAction, { kind: "chat" | "announcement" }>;
  }
  | {
    kind: "ban";
    userId: string;
    reason: string;
    onSuccess?: Extract<ModuleAction, { kind: "chat" | "announcement" }>;
    onFailure?: Extract<ModuleAction, { kind: "chat" | "announcement" }>;
  }
  | {
    kind: "overlay";
    type: string;
    elementKind: string;
    payload: Readonly<Record<string, unknown>>;
    /** Optional JSON config match resolved to overlay ids before publish. */
    recipientConfig?: { field: string; value: string };
  };

/**
 * What a module describes as the result of processing. It executes nothing
 * — per decision 0001 §13 it only describes desired actions, and only the
 * worker then executes them via Twitch, D1, or Durable Objects.
 *
 * The actions live in a shared list so their order is preserved. New
 * semantic action kinds can be added later, additively, without breaking
 * existing modules through new required fields. `diagnostics` describes the
 * business reasons for acting or not acting; the host logs executed actions
 * and their outcome.
 */
export interface ModuleResult {
  actions: readonly ModuleAction[];
  diagnostics: readonly ModuleDiagnostic[];
  /** Source labels for template values used by this result; placement is host-owned. */
  attributions?: readonly string[];
  /** Variable writes and their overlay recipients, returned by the write's D1 batch. */
  variableChanges?: readonly {
    name: string;
    value: number;
    overlayIds: readonly string[];
  }[];
}

export type ModuleActor = {
  userId: string;
  login: string;
  role: ChannelRole | null;
};

export interface ModuleMutationActor {
  userId: string;
  sessionId?: string;
}

export interface ModuleMutationAuthorization {
  sql: string;
  values: readonly (string | number | null)[];
}

type ModuleAuditValue = string | number | boolean | null | readonly string[];

/** Business values a module has explicitly cleared for the audit. */
type ModuleAuditSnapshot = Readonly<Record<string, ModuleAuditValue>>;

export interface ModuleAuditEntry {
  channelId: string;
  moduleId: string | null;
  action: AuditWriteAction;
  before: ModuleAuditSnapshot | null;
  after: ModuleAuditSnapshot | null;
}

/** The host prepares the audit part of the same D1 mutation. */
export type PrepareModuleAudit = (
  entry: ModuleAuditEntry,
  changedAt: string,
) => D1PreparedStatement;

/**
 * Writes an audit entry immediately, for an action with no accompanying D1
 * mutation to batch it with -- an external Twitch call (start a commercial,
 * create a clip), not a row change here. `PrepareModuleAudit` above stays
 * for the batched case.
 */
export type WriteModuleAudit = (
  entry: ModuleAuditEntry,
  changedAt: string,
  options?: { operationId?: string; actorUserId?: string },
) => Promise<void>;

export type AuthorizeModuleMutation = (
  channelId: string,
  actor: ModuleMutationActor,
  now: string,
) => ModuleMutationAuthorization;

/** Host-owned secret access scoped to one channel and module. */
export interface ModuleSecretAccess {
  status: (name: string) => Promise<{ configured: boolean; updatedAt: string | null }>;
  read: (name: string) => Promise<string | null>;
  /**
   * Reads a secret together with an opaque version that must still be current for dependent writes.
   * The version is the stored AES-GCM envelope: it never repeats (random IV per write, also across
   * delete and recreate), reveals no plaintext, and is only for equality guards. Never log or return it.
   */
  readWithVersion: (name: string) => Promise<{ value: string; version: string } | null>;
  /** The returned statement carries the management-role guard and can depend on the preceding batched mutation. */
  prepareWrite: (name: string, value: string, actor: ModuleMutationActor, now: string, requirePreviousChange?: boolean) => Promise<D1PreparedStatement>;
  /** The returned statement carries the management-role guard and can depend on the preceding batched mutation. */
  prepareDelete: (name: string, actor: ModuleMutationActor, now: string, requirePreviousChange?: boolean) => D1PreparedStatement;
}

/** Read-only view for resolving module template values, including version-guarded dependent writes. */
export type ModuleSecretReadAccess = Pick<ModuleSecretAccess, "status" | "read" | "readWithVersion">;

export interface ActiveChatterActivity {
  firstSeenAt: string;
  lastSeenAt: string;
}

interface ModuleTwitchUser {
  userId: string;
  login: string;
  displayName: string;
}

/** Stable identity for one scheduled timer occurrence. */
export interface ModuleAlarmIdentity {
  timerId: string;
  revision: number;
  triggerType: string;
  state: string | null;
  streamId: string | null;
  generation: number | null;
  deadline: number;
}

/** Identity and write sequence stored alongside one Durable Object alarm. */
export interface ModuleAlarmPlan {
  identity: ModuleAlarmIdentity | null;
  planSequence: number | null;
  /** Stream generation attached to this alarm, when it is stream-scoped. */
  streamGeneration?: number | null;
}

/** Infrastructure the host gives a module for its own adapter. */

export interface ModuleExecutionContext {
  DB: D1Database;
  authorizeMutation: AuthorizeModuleMutation;
  /** Ephemeral ballot access bound to the event channel and executing module. */
  ballots: ModuleBallotAccess;
  /** Point ledger access bound to the event channel and executing module. */
  points: ModulePointsAccess;
  /** Temporary VIP access bound to the event channel and executing module. */
  tempVips: ModuleTempVipAccess;
  /** Lazily reads the current channel's public blocked terms with the bot moderator token. */
  readChannelBlockedTerms?: () => Promise<readonly string[] | null>;
  /** Lazily resolves the connected bot identity so modules can ignore its own chat messages. */
  botUserId?: () => Promise<string | null>;
  /** Looks up one public Twitch user by login; throws when Helix lookup fails. */
  lookupUserByLogin?: (login: string) => Promise<ModuleTwitchUser | null>;
  /** Looks up one public Twitch user using the connected bot's token. */
  lookupUserByLoginWithBotToken?: (login: string) => Promise<ModuleTwitchUser | null>;
  /** Returns null when Twitch moderator lookup fails. */
  isChannelModerator?: (userId: string) => Promise<boolean | null>;
  /** Checks the channel's recent bot send history for an identity or exact text match. */
  isRecentBotMessage?: (senderId: string | null, text: string) => Promise<boolean>;
  /** True only when this EventSub notification committed a real stream-state transition. */
  streamStateTransitionAccepted?: boolean;
  /** Stream identity captured before an accepted offline transition changed channel state. */
  streamSession?: { streamId: string | null; startedAt: string | null };
  /** Marks this stream's anonymous word counts as ended after an accepted offline transition. */
  statisticsChat?: Pick<ModuleStatisticsChat, "markWordStreamEnded">;
  streamState: () => Promise<ModuleStreamState>;
  /** Monotonic count of accepted chat messages kept in this channel object. */
  chatActivityCount: () => Promise<number>;
  /** Distinct chatters tracked in the rolling activity window when a module declares the need. */
  activeChatters: {
    count: (windowMs: number) => Promise<number>;
    seen: (userId: string) => Promise<ActiveChatterActivity | null>;
  };
  /** Lazily loads the current channel's public Helix fields and live start time. */
  channelInfo: () => Promise<ModuleChannelInfo | null>;
  /** Lazily loads only the current channel game, independently of stream details. */
  channelGameId?: () => Promise<string | null>;
  /** Lazily loads whether the caller follows the current channel. */
  followedAt: (userId: string) => Promise<ModuleFollowedAt>;
  followerTotal: () => Promise<number | null>;
  chattersTotal: () => Promise<number | null>;
  userCreatedAt: (userId: string) => Promise<string | null>;
  readChannelVariables: (names: readonly string[]) => Promise<Readonly<Record<string, number>>>;
  renderTemplate: (
    text: string,
    moduleValues: Readonly<Record<string, string | number>>,
    changed?: { name: string; value: number },
    mode?: ModuleTemplateRenderMode,
  ) => Promise<{ text: string; diagnostics: readonly ModuleDiagnostic[]; attributions?: readonly string[] }>;
  prepareVariableChange: (
    channelId: string,
    change: { name: string; operation: ChannelVariableOperation; amount: number | null },
    now: string,
    claim: { commandName: string; revision: number; userId: string | null },
  ) => D1PreparedStatement;
  /** Host-provided cryptographically secure integer in [0, maximumExclusive). */
  secureRandomInteger: (maximumExclusive: number) => number;
  /** Lazily reads the channel's configured chat-template language. */
  channelLanguage: () => Promise<ModuleLanguage>;
  /** Lazily reads the channel's configured time zone for date/time and module conditions. */
  channelTimeZone: () => Promise<string>;
  /** Null streamGeneration marks a stream-independent timer alarm; undefined keeps legacy version guards. */
  scheduleAlarm: (
    handlerKey: string,
    alarmKey: string,
    deadline: number,
    ownerRevision?: number,
    streamGeneration?: number | null,
    identity?: ModuleAlarmIdentity,
    planSequence?: number,
  ) => Promise<void>;
  clearAlarm: (
    alarmKey: string,
    ownerRevision?: number,
    streamGeneration?: number | null,
    expectedIdentity?: ModuleAlarmIdentity | null,
    planSequence?: number,
  ) => Promise<void>;
  /** Generation captured atomically by an accepted stream-state write. */
  streamStateTransitionGeneration?: number;
  /** Reads the current execution time for a module-owned alarm key, if one is scheduled. */
  getAlarmDeadline?: (alarmKey: string) => Promise<number | null>;
  /** Reads the identity and plan sequence for a module-owned alarm key, if scheduled. */
  getAlarmPlan?: (alarmKey: string) => Promise<ModuleAlarmPlan | null>;
  /** Runs one of this module's registered alarm handlers synchronously from its event handler. */
  runModuleAlarm?: (handlerKey: string, alarmKey: string, invocation?: unknown) => Promise<unknown>;
}

export interface ModuleDryRunBallotSnapshot extends BallotSnapshot {
  /** The ballot-local term filter, available only to simulate a free-text cast. */
  blockedTerms?: readonly string[] | null;
  /** Approved free-text terms retained after their last voter switches away. */
  approvedTerms?: readonly string[];
  /** Absolute deadline from the stored ballot. */
  expiresAt?: number;
  /** Prior choice for the one simulated sender, without exposing other voter ids. */
  previousChoice?: number | string;
  /** False when the host could not resolve the sender id needed to check their prior choice. */
  previousChoiceKnown?: boolean;
  /** Whether this ballot still owns the channel-wide ballot lock. */
  isActive?: boolean;
}

/** Read-only capabilities available while a chat message is being simulated. */
export type ModuleDryRunContext = Pick<ModuleExecutionContext,
  | "DB"
  | "botUserId"
  | "streamState"
  | "channelInfo"
  | "channelGameId"
  | "channelLanguage"
  | "channelTimeZone"
  | "lookupUserByLogin"
  | "isChannelModerator"
  | "readChannelVariables"
  | "renderTemplate"
  | "followedAt"
  | "userCreatedAt"
  | "secureRandomInteger"
> & {
  /** Host-resolved default chat target, so modules do not need another lookup. */
  defaultChatTarget: ChatOutputTarget;
  /** Read-only ballot snapshots; the dry-run context never exposes ballot mutation methods. */
  ballots?: {
    readSnapshot: (ballotId: string, voterId?: string) => Promise<ModuleDryRunBallotSnapshot | null>;
    hasOpenBallotSnapshot: () => Promise<boolean>;
  };
  /** Returns true when a lookup was deliberately deferred because no valid cached credential exists. */
  isUserLookupDeferred?: (login: string) => boolean;
  /** Marks a cast as indeterminate when the sender's previous ballot choice is unknown. */
  markBallotChoiceUnresolved?: () => void;
  /** Returns true when this message's ballot result depends on an unknown previous choice. */
  isBallotChoiceUnresolved?: () => boolean;
  /** Durable Object activity reads that do not initialize tables, write state, or schedule alarms. */
  activeChatters?: Pick<ModuleExecutionContext["activeChatters"], "count" | "seen">;
  /** Reads a module cooldown without claiming or changing it. */
  pointCooldownRemaining?: (moduleId: string, key: string) => Promise<number>;
  /** Marks template rendering as a side-effect-free dry-run simulation. */
  readOnly?: boolean;
};

/** Durable storage and alarm access given to a module alarm handler. */
export interface ModuleAlarmContext {
  /** Replayable audit writes, scoped by the host to this alarm's channel and module. */
  writeModuleAudit?: WriteModuleAudit;
  DB: D1Database;
  channelId: string;
  /** Full identity and D1 plan sequence for the alarm currently being handled. */
  alarmIdentity?: ModuleAlarmIdentity | null;
  alarmPlanSequence?: number;
  /** Runs a timer occurrence in the channel object's durable scheduler. */
  runTimerOccurrence?: (
    key: string,
    deadline: number,
    ownerRevision?: number,
    identity?: ModuleAlarmIdentity | null,
    planSequence?: number,
    context?: ModuleAlarmContext,
  ) => Promise<"retain" | undefined>;
  /** Reconciles timer config and trigger inputs inside the channel object. */
  reconcileTimerSchedules?: (reason: ModuleScheduleInputChangeReason) => Promise<void>;
  /** Shared per-dispatch allowance for external provider requests. */
  externalFetchBudget?: ModuleExternalFetchBudget;
  /** Absolute statistics windows and age-bounded word counts held in the channel Durable Object. */
  statisticsChat?: ModuleStatisticsChat;
  /** Writes one privacy-safe host timeline observation with host-managed idempotency. */
  recordTimelineEvent?: (event: ModuleTimelineEvent) => Promise<void>;
  /** Writes one privacy-safe host timeline observation and throws when D1 fails. */
  recordTimelineEventStrict?: (event: ModuleTimelineEvent) => Promise<void>;
  /** Host-authenticated Helix counts; unavailable sources return null. */
  fetchViewerCount?: () => Promise<number | null>;
  fetchChattersTotal?: () => Promise<number | null>;
  /** Fetches privacy-safe clips for one completed stream; unavailable sources return null for retry. */
  fetchStreamClips?: (startedAt: string, endedAt: string) => Promise<readonly ModuleStreamClip[] | null>;
  /** Secret access bound to this alarm's channel and module. */
  secrets: ModuleSecretAccess;
  /** Ephemeral ballot access bound to this alarm's channel and module. */
  ballots: ModuleBallotAccess;
  /** Point ledger access bound to this alarm's channel and module. */
  points: ModulePointsAccess;
  /** Durable, revisioned quiz overlay state used by reconnect bootstrap and broadcast retries. */
  quizOverlaySnapshot: ModuleQuizOverlaySnapshotAccess;
  /** Adds the currently pending account welcome to a threaded game reply. */
  decoratePointWelcome?: (
    action: Extract<ModuleAction, { kind: "chat" }>,
    accountId?: string,
    claimId?: string,
    attributions?: readonly string[],
  ) => Extract<ModuleAction, { kind: "chat" }>;
  /** Channel locale and unbiased randomness for preparing the same module actions as event handlers. */
  channelLanguage: () => Promise<ModuleLanguage>;
  secureRandomInteger: (maximumExclusive: number) => number;
  /** Executes a prepared timeout through the host moderation path for alarm recovery. */
  executeTimeout: (action: Extract<ModuleAction, { kind: "timeout" }>) => Promise<ModuleTimeoutOutcome>;
  storage: {
    get(key: string): Promise<unknown>;
    put(key: string, value: unknown): Promise<void>;
    delete(key: string): Promise<boolean>;
    list?(prefix: string, limit: number, startAfter?: string): Promise<ReadonlyMap<string, unknown>>;
  };
  /** Null streamGeneration marks a stream-independent timer alarm; undefined keeps legacy version guards. */
  schedule: (
    key: string,
    deadline: number,
    ownerRevision?: number,
    streamGeneration?: number | null,
    identity?: ModuleAlarmIdentity,
    /** Serialized lifecycle modules can omit this; the ChannelObject allocates it with the alarm write. */
    planSequence?: number,
  ) => Promise<void>;
  clear: (
    key: string,
    ownerRevision?: number,
    streamGeneration?: number | null,
    expectedIdentity?: ModuleAlarmIdentity | null,
    planSequence?: number,
  ) => Promise<void>;
  /** Stream generation attached to this alarm entry, when it is stream-scoped. */
  streamGeneration?: number;
  /** Reads the current execution time for a module-owned alarm key, if one is scheduled. */
  getAlarmDeadline?: (key: string) => Promise<number | null>;
  /** Reads the identity and plan sequence for a module-owned alarm key, if scheduled. */
  getAlarmPlan?: (key: string) => Promise<ModuleAlarmPlan | null>;
  /** Renders a host template in the channel's event context. */
  renderTemplate: (
    text: string,
    moduleValuesOrNow?: Readonly<Record<string, string | number>> | number,
    nowOrModuleValues?: number | Readonly<Record<string, string | number>>,
  ) => Promise<{ text: string; diagnostics?: readonly ModuleDiagnostic[]; attributions?: readonly string[] }>;
  /** Publishes a module-owned overlay message to enabled elements in this channel. */
  publishModuleOverlayMessage: (
    type: string,
    elementKind: string,
    payload: Readonly<Record<string, unknown>>,
  ) => Promise<void>;
  /** Sends scheduled output with an occurrence claim; automated output uses the shared channel limit by default. */
  sendChat: (
    text: string,
    idempotencyKey: string,
    attributions?: readonly string[],
    stillValid?: () => Promise<boolean>,
    target?: ChatOutputTarget,
    options?: {
      replyToMessageId?: string;
      sourceBroadcasterUserId?: string;
      automated?: boolean;
      pointWelcome?: { claimId: string; operationId: string; text: string };
    },
  ) => Promise<{
    sent: boolean;
    reason: string | null;
    retryable: boolean;
    delivery: "delivered" | "rate_limited" | "ambiguous" | "not_attempted" | "failed";
    attemptedAt?: number;
    postedWelcomeClaimId?: string;
    postedWelcomeOperationId?: string;
  }>;
  /** Monotonic count of accepted chat messages kept in this channel object. */
  chatActivityCount: () => Promise<number>;
  /** Resolves all registered module event-time sources without exposing module identity. */
  resolveEventTimes: (now: number) => Promise<readonly ResolvedModuleEventTime[]>;
  streamState: () => Promise<ModuleStreamState>;
  streamStartedAt: () => Promise<{ streamId: string | null; startedAt: string | null }>;
  /** Writes diagnostics through the host event-log boundary. */
  writeDiagnostics?: (triggerId: string, diagnostics: readonly ModuleDiagnostic[], now: string) => Promise<unknown>;
}

export interface ModuleStatisticsWindow {
  streamId: string | null;
  windowStart: string;
  messages: number;
  emotes: number;
  viewerCount: number | null;
  chatters: number | null;
  activeChatters: number | null;
  metricsSampled: boolean;
  revision: number;
}

export interface ModuleStatisticsWordCount {
  word: string;
  count: number;
  lastSeenAt: string;
}

export interface ModuleStatisticsWordBucket {
  streamId: string;
  batch: number;
}

export interface ModuleStatisticsClipImport {
  streamId: string;
  startedAt: string | null;
  endedAt: string;
}

/** Clip fields safe for long-term timeline storage. Twitch creator and game metadata is excluded. */
export interface ModuleStreamClip {
  clipId: string;
  title: string;
  createdAt: string;
  viewCount?: number;
  durationSeconds?: number;
}

export type ModuleStatisticsStreamState = "online" | "offline" | "unknown";

interface ModuleTimelineEventBase {
  identity: string;
  occurredAt: string;
}

export type ModuleTimelineEvent =
  | (ModuleTimelineEventBase & { kind: "stream_online"; streamId: string | null; detail: { startedAt: string | null } })
  | (ModuleTimelineEventBase & { kind: "stream_offline"; detail: Record<string, never> })
  | (ModuleTimelineEventBase & {
      kind: "channel_metadata";
      detail: { title: string | null; category: { id: string; name: string | null } | null };
    })
  | (ModuleTimelineEventBase & {
      kind: "raid_incoming";
      detail: { viewers: number; partner: { id: string; login: string; displayName: string } };
    })
  | (ModuleTimelineEventBase & {
      kind: "raid_outgoing";
      detail: { viewers: number; partner: { id: string; login: string; displayName: string } };
    })
  | (ModuleTimelineEventBase & {
      kind: "clip_created";
      detail: {
        clipId: string;
        title?: string;
        createdAt?: string;
        viewCount?: number;
        durationSeconds?: number;
      };
    })
  | (ModuleTimelineEventBase & { kind: "ad_break"; detail: { durationSeconds: number; startedAt: string } })
  | (ModuleTimelineEventBase & {
      kind: "emote_spike";
      detail: {
        windowStart: string;
        emotes: number;
        medianEmotes: number;
        baselineWindows: number;
        minimumEmotes: number;
        multiplier: number;
      };
    });

interface ModuleStatisticsChat {
  advanceToBoundary: (boundary: number, streamIsOnline: boolean, streamId: string | null) => Promise<void>;
  listDirtyCompletedWindows: () => Promise<readonly string[]>;
  readWindow: (windowStart: string) => Promise<ModuleStatisticsWindow | null>;
  saveWindowMetrics: (windowStart: string, viewerCount: number | null, chatters: number | null) => Promise<void>;
  markWindowClean: (windowStart: string, revision: number) => Promise<void>;
  pruneExpiredWindows: (now: number) => Promise<void>;
  hasWindows: () => Promise<boolean>;
  flushWordCounts: () => Promise<void>;
  markWordStreamEnded: (streamId: string, endedAt: string) => Promise<void>;
  reconcileWordStreams: (streamState: ModuleStatisticsStreamState, streamId: string | null, observedAt: number) => Promise<void>;
  listMergeableWordBuckets: (
    streamState: ModuleStatisticsStreamState,
    streamId: string | null,
    idleBefore: number,
  ) => Promise<readonly ModuleStatisticsWordBucket[]>;
  prepareWordBucketForMerge: (
    bucket: ModuleStatisticsWordBucket,
    streamState: ModuleStatisticsStreamState,
    streamId: string | null,
    idleBefore: number,
  ) => Promise<readonly ModuleStatisticsWordCount[]>;
  deleteWordBucket: (bucket: ModuleStatisticsWordBucket) => Promise<void>;
  pruneExpiredWordStreams: (now: number) => Promise<void>;
  hasWordStreams: () => Promise<boolean>;
  markClipImportPending: (streamId: string, startedAt: string | null, endedAt: string) => Promise<void>;
  listReadyClipImports: (now: number) => Promise<readonly ModuleStatisticsClipImport[]>;
  completeClipImport: (streamId: string, endedAt: string) => Promise<void>;
  pruneExpiredClipImports: (now: number) => Promise<void>;
  hasClipImports: () => Promise<boolean>;
}

type ModuleTimeoutOutcome = "applied" | "rejected" | "ambiguous" | "suppressed";

export interface ModuleEventTimeContext {
  DB: D1Database;
  channelId: string;
  now: number;
  channelTimeZone: () => Promise<string>;
  channelLocation: () => Promise<ModuleChannelLocation | null>;
}

interface ModuleEventTimeSource {
  /** Local to the declaring module; the host adds a stable namespace. */
  id: string;
  label: Readonly<Record<ModuleLanguage, string>>;
  resolve: (context: ModuleEventTimeContext) => Promise<readonly string[]>;
}

export interface ResolvedModuleEventTime {
  id: string;
  label: Readonly<Record<ModuleLanguage, string>>;
  at: string;
}

/**
 * A module-owned alarm handler, registered once and selected by stable key.
 * Handlers must be idempotent because the host retries failures using this backoff.
 */
export interface ModuleAlarmDefinition {
  key: string;
  /** Milliseconds between retries after consecutive failures. The last delay is reused. */
  retryDelaysMs?: readonly number[];
  handle: (
    context: ModuleAlarmContext,
    alarmKey: string,
    deadline: number,
    ownerRevision?: number,
    invocation?: unknown,
  ) => Promise<unknown>;
  /** Replans durable schedules after an input owned by the host or another module changes. */
  onScheduleInputsChanged?: (
    context: ModuleAlarmContext,
    reason: ModuleScheduleInputChangeReason,
  ) => Promise<void>;
}

export interface ModulePointRoundResult {
  roundId: string;
  data: unknown;
  result: unknown;
  resolvedAt: string;
}

export interface ModulePointRoundDeadline {
  roundId: string;
  data: unknown;
  deadlineAt: number;
}

export interface ModulePointRoundAnnouncement {
  roundId: string;
  data: unknown;
  text: string;
}

/**
 * `activation` fires when a channel control or module activation change may
 * have left a stream-scoped schedule unarmed: the host dispatches events to
 * a module only while it is enabled and the channel is unpaused (see
 * `selectModulesForEvent`), so a `stream.online` notification received
 * while either was off never reaches the module at all.
 */
export type ModuleScheduleInputChangeReason = "event_times" | "channel_time_zone" | "activation" | "stream_state";

export interface ModuleTemplateContentCandidate {
  name: string;
  texts: readonly string[];
  /** Existing consumer references are rewritten to this candidate during a text-block rename. */
  renamedFrom?: string;
}

/** A generic reason a module consumer rejects a proposed template-content edit. */
export interface ModuleTemplateContentIssue {
  reason: "input_dependent";
  consumerName: string;
}

export interface ModuleTemplateContentValidationContext {
  DB: D1Database;
  channelId: string;
  candidate: ModuleTemplateContentCandidate;
  registeredVariables: readonly ModuleRegisteredTemplateVariable[];
}

export type ModuleFollowedAt = (string & {}) | null | "unavailable";

export interface BallotSnapshot {
  counts: readonly number[];
  revision: number;
  /** Generic bounded free-text tally for modules that open a term ballot. */
  terms?: readonly BallotTermCount[];
  /** Incoming distinct terms ignored after the term cap was reached. */
  more?: number;
  /** False when the host could not load the blocked-term list for this ballot. */
  termFilterReady?: boolean;
  /** Present once the host has finalized the ballot. */
  outcome?: Exclude<BallotFinalizeOutcome, "open">;
}

export interface BallotTermCount {
  term: string;
  count: number;
  approved: boolean;
}

export interface BallotTermFilter {
  /** Null means the host could not load the channel's blocked-term list. */
  blockedTerms: readonly string[] | null;
}

export type BallotOpenResult =
  | { status: "opened" }
  | { status: "busy"; moduleId: string };

export type BallotCastResult = BallotSnapshot & {
  status: "counted" | "changed" | "unchanged" | "not_open" | "blocked" | "overflow";
};

export type BallotFinalizeOutcome = "open" | "passed" | "expired" | "not_open";

/** Data-only pass predicate evaluated atomically against the host's current tally. */
export interface BallotFinalizeRule {
  passIf: {
    yes: number;
    no: number;
    netAtLeast: number;
  };
}

export type BallotFinalizeResult = Omit<BallotSnapshot, "outcome"> & { outcome: BallotFinalizeOutcome };

/** Ballot access already bound by the host to one channel and one module. */
export interface ModuleBallotAccess {
  open: (ballotId: string, optionCount: number, expiresAt: number, rule?: BallotFinalizeRule, termFilter?: BallotTermFilter) => Promise<BallotOpenResult>;
  cast: (ballotId: string, userId: string, choice: number, options?: { onlyIfNew?: boolean }) => Promise<BallotCastResult>;
  /** Casts a normalized text term while storing only the ballot-local voter hash. */
  castTerm?: (ballotId: string, userId: string, term: string, matchText?: string) => Promise<BallotCastResult>;
  /** Refreshes a term ballot's blocked-term filter and removes matching aggregate terms. */
  setBlockedTerms?: (ballotId: string, blockedTerms: readonly string[]) => Promise<BallotSnapshot | null>;
  /** Remembers an approved term for this ballot only. */
  approveTerm?: (ballotId: string, term: string) => Promise<{ status: "approved" | "blocked" | "not_open" | "unavailable"; snapshot: BallotSnapshot | null }>;
  read: (ballotId: string) => Promise<BallotSnapshot | null>;
  /** Reads whether any module currently owns the channel's exclusive ballot. */
  hasOpenBallot?: () => Promise<boolean>;
  /** Reads a snapshot without finalizing an expired ballot or changing its alarm. */
  readSnapshot?: (ballotId: string) => Promise<ModuleDryRunBallotSnapshot | null>;
  /** Reads channel-wide availability without finalizing or cleaning up a ballot. */
  hasOpenBallotSnapshot?: () => Promise<boolean>;
  close: (ballotId: string) => Promise<BallotSnapshot | null>;
  /** Atomically finalizes using the stored pass rule and returns the outcome and snapshot. */
  finalize: (ballotId: string) => Promise<BallotFinalizeResult>;
  /** Releases an idempotent close snapshot after the module has persisted its result. */
  acknowledgeClosed?: (ballotId: string) => Promise<void>;
}

export type PointsKind = "bonus" | "activity" | "safety_net" | "stake" | "game" | "resolve" | "refund" | "admin";

export type PointWelcomeKind = "start_bonus" | "safety_net";

export interface StoredPointWelcome {
  accountId: string;
  operationId: string;
  messageId: string;
  text: string;
  kind: PointWelcomeKind;
}

export type PointsMovement =
  | { accountId: string; delta: number; login?: string }
  | { accountId: string; potShare: { pot: string; numerator: number; denominator: number } };

export interface PointsApplyResult {
  replayed: boolean;
  result: unknown;
  balances: Record<string, number>;
}

export interface PointSettlementCooldown {
  key: string;
  seconds: number;
}

export type PointJoinResult =
  | { status: "joined"; replayed: boolean }
  | { status: "already_joined" | "round_closed" };

export interface PointRoundAnswerResult {
  status: "recorded" | "already_answered" | "round_closed";
  replayed: boolean;
  first: boolean;
}

export interface PointActiveRound {
  roundId: string;
  state: "announcing" | "open";
  data: unknown;
  deadlineAt: number | null;
  createdAt?: string;
}

export interface PointResolvedRound {
  roundId: string;
  data: unknown;
  result: unknown;
  resolvedAt: string;
  createdAt?: string;
}

/** Funded participants of one round: total count and stake, plus the first logins in join order. */
export interface PointRoundParticipants {
  count: number;
  totalStake: number;
  logins: string[];
}

interface PointRoundParticipantRecord {
  accountId: string;
  login: string;
  stake: number;
  data: unknown;
}

export interface PointRoundChoiceResult {
  status: "recorded" | "round_closed";
  replayed: boolean;
}

export interface PointRoundAttackResult {
  status: "hit" | "cooldown" | "round_closed";
  replayed: boolean;
  damage: number;
  defeated: boolean;
  currentHp: number;
  maxHp: number;
  attackers: number;
  damageDealt: number;
}

export interface ModulePointsAccess {
  /** Optional stable request identity for operations whose persisted result contains server-generated metadata. */
  apply: (operationId: string, kind: PointsKind, movements: PointsMovement[], result: unknown, payloadIdentity?: unknown) => Promise<PointsApplyResult>;
  /** Runs an instant game settlement against the actor balance and jackpot snapshot in one ledger transaction. */
  settle: (
    operationId: string,
    kind: string,
    actor: { accountId: string; login: string },
    stake: number,
    params: unknown,
    options: { payloadIdentity: unknown; cooldown?: PointSettlementCooldown },
  ) => Promise<PointsApplyResult>;
  touchAccount: (accountId: string, login: string, startBonus: number) => Promise<{
    welcomePending: boolean;
    startBonus: number | null;
  }>;
  getPendingWelcome?: (accountId: string) => Promise<StoredPointWelcome | null>;
  /** Stores the note payload while the account's pending bit remains the eligibility source. */
  storePendingWelcome?: (welcome: StoredPointWelcome) => Promise<void>;
  claimWelcome?: (accountId: string, operationId: string, claimId: string) => Promise<StoredPointWelcome | null>;
  releaseWelcome?: (accountId: string, operationId: string, claimId: string) => Promise<void>;
  ackWelcome: (accountId: string, operationId: string, claimId?: string) => Promise<void>;
  prepareChatActivity: (
    accountId: string,
    login: string,
    startBonus: number,
    messageAt: number,
    live: boolean,
    activityRules: { amount: number; windowMinutes: number; dailyCap: number },
    safetyNetRules: { amount: number; threshold: number } | null,
    welcomes: {
      messageId: string;
      language: ModuleLanguage;
      currencyName: string;
      safetyNetText: string;
    },
  ) => Promise<{
    accountExisted: boolean;
    welcomePending: boolean;
    startBonus: number | null;
    pendingWelcome: StoredPointWelcome | null;
  }>;
  creditActivity: (
    accountId: string,
    login: string,
    messageAt: number,
    live: boolean,
    rules: { amount: number; windowMinutes: number; dailyCap: number },
  ) => Promise<{ credited: number }>;
  creditSafetyNet?: (
    accountId: string,
    login: string,
    messageAt: number,
    live: boolean,
    rules: { amount: number; threshold: number },
    welcome: Omit<StoredPointWelcome, "accountId" | "kind">,
  ) => Promise<{ credited: number }>;
  balance: (accountId: string) => Promise<number>;
  getAccount: (accountId: string) => Promise<{ accountId: string; login: string; balance: number } | null>;
  listAccounts: (query: { sort: "balance" | "month"; search?: string; cursor?: string; limit: number }) => Promise<{
    rows: { accountId: string; login: string; balance: number; monthNet: number | null }[];
    cursor?: string;
  }>;
  history: (accountId: string, cursor?: string, limit?: number) => Promise<{
    rows: { operationId: string; kind: string; moduleId: string; delta: number; balanceAfter: number; at: string; detail: unknown }[];
    cursor?: string;
  }>;
  summary: () => Promise<{ circulation: number; jackpots: Record<string, number>; inRounds: number; accounts: number; month: string }>;
  ranking: (month: string, limit: number) => Promise<{ accountId: string; login: string; net: number }[]>;
  rankOf: (accountId: string, month: string) => Promise<{ rank: number | null; net: number }>;
  markAuditWritten: (operationId: string) => Promise<void>;
  pendingAudits: () => Promise<{ operationId: string; detail: unknown }[]>;
  claimCooldown: (key: string, seconds: number) => Promise<boolean>;
  rounds: {
    open: (roundId: string, data: unknown, quizOverlaySnapshot?: Readonly<Record<string, unknown>>) => Promise<ModuleQuizOverlaySnapshot | undefined>;
    openWithStake: (
      roundId: string,
      data: unknown,
      operationId: string,
      accountId: string,
      login: string,
      stake: number,
      cooldown?: PointSettlementCooldown,
    ) => Promise<{ roundId: string; replayed: boolean }>;
    active: () => Promise<PointActiveRound | null>;
    listOpen: () => Promise<PointActiveRound[]>;
    recentlyResolved: (maxAgeMs: number) => Promise<PointResolvedRound | null>;
    markAnnounced: (roundId: string, deadlineAt: number, alarmKey: string) => Promise<void>;
    join: (roundId: string, operationId: string, accountId: string, login: string, stake: number, data: unknown) => Promise<PointJoinResult>;
    /** Reads the round's participant count and total stake, with at most `nameLimit` logins. */
    participants: (roundId: string, nameLimit: number) => Promise<PointRoundParticipants>;
    /** Reads participant data inside the channel Durable Object for round reveal and settlement work. */
    participantRecords: (roundId: string) => Promise<PointRoundParticipantRecord[]>;
    /** Replaces one participant's response for the active quiz question, keyed by the chat message operation id. */
    answerChoice: (
      roundId: string,
      operationId: string,
      accountId: string,
      login: string,
      questionIndex: number,
      choice: number,
      messageAt: number,
    ) => Promise<PointRoundChoiceResult>;
    /** Persists module round state and resets its shared deadline/alarm; a null deadline pauses the round. */
    update: (
      roundId: string,
      data: unknown,
      deadlineAt: number | null,
      quizOverlaySnapshot?: Readonly<Record<string, unknown>>,
    ) => Promise<ModuleQuizOverlaySnapshot | undefined>;
    answer: (roundId: string, operationId: string, accountId: string, login: string, graceS: number, messageAt: number) => Promise<PointRoundAnswerResult>;
    attack: (roundId: string, operationId: string, accountId: string, login: string, damage: number, messageAt: number) => Promise<PointRoundAttackResult>;
    resolve: (roundId: string, settlement: { kind: string; params: unknown }) => Promise<PointsApplyResult>;
    accept: (roundId: string, operationId: string, accountId: string, login: string, replyToMessageId?: string) => Promise<PointsApplyResult>;
    cancel: (roundId: string, quizOverlaySnapshot?: Readonly<Record<string, unknown>>) => Promise<number>;
    cancelOpenRounds: (revision: number, quizOverlaySnapshot?: Readonly<Record<string, unknown>>) => Promise<number>;
    setGate: (closed: boolean, revision: number, lastDisabledRevision?: number | null) => Promise<{ applied: boolean }>;
  };
}

export type TempVipSource = "command" | "dashboard" | "import" | "channel_points" | "observed";
export type TempVipEndReason = "expired" | "ended_early" | "removed_on_twitch";
export type ModuleTempVipMode = "observe" | "managed" | "inactive";
type ModuleTempVipClassification = "unclassified" | "permanent" | "temporary";
type ModuleTempVipConfirmation = "confirmed" | "awaiting_vip";

export interface ModuleTempVipPerson {
  userId: string;
  login: string;
  displayName: string;
}

export interface ModuleTempVipRow extends ModuleTempVipPerson {
  expiresAt: string | null;
  grantedAt: string;
  grantedByUserId: string | null;
  grantedByLogin: string | null;
  source: TempVipSource;
  removalFailedAt: string | null;
  removalAttempts: number;
  removalError: string | null;
  endReason: "ended_early" | null;
  classification: ModuleTempVipClassification;
  confirmation: ModuleTempVipConfirmation;
  expiryReminderAt: string | null;
}

export interface ModuleTempVipResult {
  status: string;
  reason?: string;
  allowedDurationMs?: number;
  retryAt?: string;
  expiresAt?: string;
  row?: ModuleTempVipRow;
}

export interface ModuleTempVipGrantRequest {
  operationId: string;
  person: ModuleTempVipPerson;
  grantedBy: { userId: string; login: string } | null;
  durationMs: number;
  maximumRemainingMs: number;
  source: TempVipSource;
}

export interface ModuleTempVipExtendRequest {
  operationId: string;
  person: ModuleTempVipPerson;
  grantedBy?: { userId: string; login: string } | null;
  durationMs: number;
  maximumRemainingMs: number;
  source?: TempVipSource;
}

export interface ModuleTempVipImportRequest extends ModuleTempVipGrantRequest {
  source: "import";
  /** Absolute expiry for a channel-local date entry; the queue must preserve it as-is. */
  expiresAt?: string;
  /** Server validation instant for an exact-minimum date entry. The queue omits this from payload identity. */
  expiresAtValidatedAt?: string;
}

export interface ModuleTempVipOperationRequest {
  operationId: string;
  userId: string;
}

export interface ModuleTempVipRemovedEvent {
  triggerId: string;
  userId: string;
  eventSubTimestamp: string;
}

export interface ModuleTempVipModerationEvent {
  triggerId: string;
  action: "vip" | "unvip";
  person: ModuleTempVipPerson;
  moderatorUserId: string;
  eventSubTimestamp: string;
}

export interface ModuleTempVipIntentRequest {
  operationId: string;
  person: ModuleTempVipPerson;
  grantedBy: { userId: string; login: string } | null;
  expiresAt: string;
  durationMs?: number;
  maximumRemainingMs?: number;
  intentAt?: string;
}

export interface ModuleTempVipObservedAddRequest {
  operationId: string;
  person: ModuleTempVipPerson;
  actor: { userId: string; login: string };
}

export interface ModuleTempVipClassifyRequest {
  operationId: string;
  userId: string;
  classification: "permanent" | "temporary";
  expiresAt: string | null;
  /** Relative requests stay relative in the durable operation so retries replay identically. */
  durationMs?: number;
  actor: { userId: string; login: string };
}

/** Channel-bound temporary VIP storage and Twitch operations owned by the host. */
export interface ModuleTempVipAccess {
  mode: () => Promise<ModuleTempVipMode>;
  recordIntent: (request: ModuleTempVipIntentRequest) => Promise<ModuleTempVipResult>;
  addObserved: (request: ModuleTempVipObservedAddRequest) => Promise<ModuleTempVipResult>;
  classify: (request: ModuleTempVipClassifyRequest) => Promise<ModuleTempVipResult>;
  handleModeration: (event: ModuleTempVipModerationEvent) => Promise<ModuleTempVipResult>;
  grant: (request: ModuleTempVipGrantRequest) => Promise<ModuleTempVipResult>;
  extend: (request: ModuleTempVipExtendRequest) => Promise<ModuleTempVipResult>;
  importExisting: (request: ModuleTempVipImportRequest) => Promise<ModuleTempVipResult>;
  replayImport: (operationId: string, userId: string, expiresAt: string) => Promise<ModuleTempVipResult | null>;
  end: (request: ModuleTempVipOperationRequest) => Promise<ModuleTempVipResult>;
  retryRemovalNow: (userId: string) => Promise<ModuleTempVipResult>;
  release: (request: ModuleTempVipOperationRequest) => Promise<ModuleTempVipResult>;
  list: () => Promise<ModuleTempVipRow[]>;
  runExpiry: (now?: string) => Promise<{ removed: number; queued: number }>;
  handleRemoved: (event: ModuleTempVipRemovedEvent) => Promise<ModuleTempVipResult>;
}

/** Route access binds the durable VIP queue to one channel and adds read-only Twitch VIP discovery. */
export type ModuleTempVipRouteAccess = Pick<
  ModuleTempVipAccess,
  "mode" | "addObserved" | "classify" | "grant" | "extend" | "importExisting" | "replayImport" | "end" | "retryRemovalNow" | "release" | "list"
> & {
  listExisting: () => Promise<
    | { ok: true; people: ModuleTempVipPerson[] }
    | { ok: false; reason: string }
  >;
};

export type ModuleStreamState = "online" | "offline" | "unknown";

export const MODULE_NAVIGATION_CATEGORIES = ["chat", "participation", "games", "twitch", "data", "stream", "admin"] as const;
export type ModuleNavigationCategory = (typeof MODULE_NAVIGATION_CATEGORIES)[number];

interface ModuleHowToPlayDefinition {
  /** Immediate cockpit actions that start a game; labels and live conditions come from the action declarations. */
  cockpitActionIds?: readonly string[];
  /** Boolean setting that enables automatic game starts. */
  autoStartSetting?: string;
  /** Optional interval setting used with automatic starts. */
  autoStartIntervalSetting?: string;
  /** Localized participation instructions not represented by a chat command. */
  participation?: Readonly<Record<ModuleLanguage, string>>;
  /** A fixed participation detail whose wording depends on a setting, such as moderator eligibility. */
  participationSetting?: {
    setting: string;
    enabled: Readonly<Record<ModuleLanguage, string>>;
    disabled: Readonly<Record<ModuleLanguage, string>>;
  };
}

export interface ModuleChannelInfo {
  title: string;
  gameName: string;
  gameId: string;
  startedAt: string | null;
  viewerCount: number;
}

/** A localized, module-owned item in the channel navigation. */
interface ModuleNavigationEntry {
  id: string;
  label: Readonly<Record<ModuleLanguage, string>>;
  description?: Readonly<Record<ModuleLanguage, string>>;
  /** Hide the module's main switch when this page is permanently available. */
  showMainSwitch?: boolean;
  iconKind: string;
  keywords?: readonly string[];
}

/** Path data for the module's dashboard glyph; the host renders every descriptor through the same SVG frame. */
interface ModuleIconDescriptor {
  paths: readonly string[];
}

/** A module-owned group used to present its template variables in the dashboard. */
export interface ModuleTemplateVariableGroup {
  label: Readonly<Record<ModuleLanguage, string>>;
  icon: ModuleIconDescriptor;
  /** Stable ordering relative to host groups and other module groups. */
  order?: number;
}

/** A module-owned source of text that can contain template-variable references. */
export interface ModuleTemplateUsageSource {
  text: string;
  kind: "command" | "faq" | "timer" | "overlay" | "event";
  label: string;
}

export type ModuleTemplateRenderMode = "chat" | "preview" | "overlay";

/** Host-owned channel location shared read-only with modules. */
export interface ModuleChannelLocation {
  readonly name: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly timeZone: string;
}

/** Host services available to a module while resolving its declared template values. */
export interface ModuleTemplateValueContext {
  DB: D1Database;
  channelId: string;
  /** Read-only secret access bound to the provider's channel and module. */
  secrets: ModuleSecretReadAccess;
  templateContext: TemplateContext;
  knownTemplateVariableNames: ReadonlySet<string>;
  chatStatus: readonly ModuleChatStatus[] | null;
  mode: ModuleTemplateRenderMode;
  channelLanguage: () => Promise<ModuleLanguage>;
  streamState: () => Promise<ModuleStreamState>;
  channelInfo: () => Promise<ModuleChannelInfo | null>;
  channelGameId?: () => Promise<string | null>;
  channelTimeZone: () => Promise<string>;
  /** Lazily reads the host-owned channel location. */
  channelLocation: () => Promise<ModuleChannelLocation | null>;
  /** The public Worker origin, used by modules to reject requests back to this service. */
  publicOrigin?: string;
  /** Shared across the providers involved in a single template render. */
  externalFetchBudget?: ModuleExternalFetchBudget;
  /** Resolves provider values from cache only and suppresses provider status writes. */
  readOnly?: boolean;
  /** Marks template resolution performed as part of a chat dry run. */
  dryRun?: boolean;
  /** Simulated last-chosen indices shared by all renders in one dry-run request. */
  textBlockSelectionState?: Map<string, number>;
  /** Runs a registered module alarm through its channel Durable Object. */
  runModuleAlarm?: (moduleId: string, handlerKey: string, alarmKey: string, invocation?: unknown) => Promise<unknown>;
  /** Renders a module-owned nested fragment with the same host values and channel context. */
  renderTemplate: (text: string, mode?: ModuleTemplateRenderMode) => Promise<{ text: string; diagnostics: readonly ModuleDiagnostic[]; attributions?: readonly string[] }>;
  addDiagnostic: (diagnostic: ModuleDiagnostic) => void;
  now: number;
  /** Resolves declared data-source conditions only when a block uses them. */
  resolveTemplateConditions: (ids: readonly string[]) => Promise<Readonly<Record<string, string>>>;
  /** Values preserved for overlay-side countdown or other browser calculations. */
  dynamicTemplateVariableNames?: ReadonlySet<string>;
  /** Command input is present only while resolving a chat-command template. */
  commandInput?: { commandName: string; arguments: string; usageText?: string };
  /** Records a source label for values used in this render; the host handles output placement. */
  addTemplateValueAttribution?: (text: string) => void;
}

export interface ModuleTemplateConditionContext {
  DB: D1Database;
  channelId: string;
  channelTimeZone: () => Promise<string>;
  /** Lazily reads the host-owned channel location. */
  channelLocation: () => Promise<ModuleChannelLocation | null>;
  /** The public Worker origin, used by modules to reject requests back to this service. */
  publicOrigin?: string;
  /** Shared across the providers involved in a single template render. */
  externalFetchBudget?: ModuleExternalFetchBudget;
  /** Prevents provider lookups or cache fills when resolving a panel read. */
  readOnly?: boolean;
  /** Marks template condition resolution performed as part of a chat dry run. */
  dryRun?: boolean;
  now: number;
  commandInput?: { commandName: string; arguments: string; usageText?: string };
  /** Records a source label when a condition uses values from this module. */
  addTemplateValueAttribution?: (text: string) => void;
  /** Adds a note to the current template evaluation. */
  addDiagnostic?: (diagnostic: ModuleDiagnostic) => void;
  /** Records the UTC instant when a resolved condition value may next change, for overlay refresh scheduling. */
  addTemplateConditionNextChangeAt?: (at: string) => void;
  /** Flags a transient lookup failure so the host schedules a short retry, without rejecting the batch. */
  reportTemplateLookupFailure?: () => void;
}

export interface ModuleExternalFetchBudget {
  /** Claims one outbound HTTP request, including a redirect hop. */
  claim: () => boolean;
}

export interface ModuleTemplateConditionTimelineContext extends ModuleTemplateConditionContext {
  until: number;
}

/** A condition a module makes available to text block variants. */
export interface ModuleTextBlockConditionDefinition {
  id: string;
  label: Readonly<Record<ModuleLanguage, string>>;
  values: Readonly<Record<string, Readonly<Record<ModuleLanguage, string>>>>;
  /** The value can change with time without a server-side mutation. */
  timeDependent?: boolean;
}

export interface ModuleVariableReferenceUsage {
  moduleId: string;
  itemName: string;
  kind: "action" | "template" | "display";
  overlayId?: string;
  elementId?: string;
  elementLabel?: string;
  reconnect?: boolean;
}

export interface ModuleVariableReferences {
  usages: (db: D1Database, channelId: string, name: string) => Promise<readonly ModuleVariableReferenceUsage[]>;
  /** Read usages for a complete channel variable list with one query per module. */
  usagesForVariables?: (
    db: D1Database,
    channelId: string,
    names: readonly string[],
  ) => Promise<ReadonlyMap<string, readonly ModuleVariableReferenceUsage[]>>;
  validateRename?: (
    db: D1Database,
    channelId: string,
    from: string,
    to: string,
  ) => Promise<ModuleVariableRenameValidation>;
  rename: (
    db: D1Database,
    channelId: string,
    from: string,
    to: string,
    guard?: ModuleVariableRenameGuard,
  ) => readonly D1PreparedStatement[];
  afterRename?: (db: D1Database, channelId: string, from: string, to: string) => void;
}

export interface ModuleVariableRenameGuard {
  value: number;
  description: string;
  resetOnStreamStart: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ModuleVariableRenamePrecondition {
  sql: string;
  values: readonly unknown[];
}

interface ModuleVariableRenameValidation {
  usages: readonly ModuleVariableReferenceUsage[];
  precondition: ModuleVariableRenamePrecondition;
}

export const moduleVariableRenameTarget = (
  channelId: string,
  name: string,
  guard?: ModuleVariableRenameGuard,
): { sql: string; values: readonly unknown[] } => guard === undefined
  ? {
    sql: "EXISTS (SELECT 1 FROM channel_variables WHERE channel_id = ? AND name = ?)",
    values: [channelId, name],
  }
  : {
    sql: `EXISTS (
      SELECT 1 FROM channel_variables
       WHERE channel_id = ? AND name = ? AND value = ? AND description = ?
         AND reset_on_stream_start = ? AND created_at = ? AND updated_at = ?
    )`,
    values: [channelId, name, guard.value, guard.description, guard.resetOnStreamStart ? 1 : 0, guard.createdAt, guard.updatedAt],
  };

export interface ModuleChannelVariable {
  name: string;
  value: number;
  description: string;
}

/** Host-backed variable access for module routes; each call is channel-scoped. */
interface ModuleChannelVariableAccess {
  listChannelVariables: (channelId: string) => Promise<readonly ModuleChannelVariable[]>;
  findChannelVariable: (channelId: string, name: string) => Promise<ModuleChannelVariable | null>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Finds and rewrites channel-variable template references in module settings. */
export const settingsVariableReferences = (
  moduleId: string,
  fields: readonly string[],
): ModuleVariableReferences => ({
  async usages(db, channelId, name): Promise<readonly ModuleVariableReferenceUsage[]> {
    const row = await db.prepare(
      "SELECT settings FROM channel_modules WHERE channel_id = ? AND module_id = ?",
    ).bind(channelId, moduleId).first<{ settings: string }>();
    if (row === null) return [];
    let settings: unknown;
    try {
      settings = JSON.parse(row.settings) as unknown;
    } catch {
      return [];
    }
    if (!isRecord(settings)) return [];
    const token = channelVariableTemplateToken(name);
    return fields.flatMap((field) => {
      const value = settings[field];
      return typeof value === "string" && value.includes(token)
        ? [{ moduleId, itemName: field, kind: "template" as const }]
        : [];
    });
  },
  async usagesForVariables(db, channelId, names) {
    const result = new Map<string, ModuleVariableReferenceUsage[]>(names.map((name) => [name, []]));
    if (names.length === 0) return result;
    const row = await db.prepare(
      "SELECT settings FROM channel_modules WHERE channel_id = ? AND module_id = ?",
    ).bind(channelId, moduleId).first<{ settings: string }>();
    if (row === null) return result;
    let settings: unknown;
    try {
      settings = JSON.parse(row.settings) as unknown;
    } catch {
      return result;
    }
    if (!isRecord(settings)) return result;
    for (const name of names) {
      const usages = result.get(name);
      if (usages === undefined) continue;
      const token = channelVariableTemplateToken(name);
      for (const field of fields) {
        const value = settings[field];
        if (typeof value === "string" && value.includes(token)) usages.push({ moduleId, itemName: field, kind: "template" });
      }
    }
    return result;
  },
  rename(db, channelId, from, to, guard) {
    const oldToken = channelVariableTemplateToken(from);
    const nextToken = channelVariableTemplateToken(to);
    const target = moduleVariableRenameTarget(channelId, to, guard);
    return [prepareChannelModuleSettingsWrite(
      db,
      channelId,
      moduleId,
      "replace(settings, ?, ?)",
      [oldToken, nextToken],
      `AND instr(settings, ?) > 0
        AND ${target.sql}
        AND NOT EXISTS (SELECT 1 FROM channel_variables WHERE channel_id = ? AND name = ?)`,
      [oldToken, ...target.values, channelId, from],
    )];
  },
});

export interface ModuleColumnVariableReferencesOptions {
  moduleId: string;
  tableName: string;
  idColumn: string;
  itemNameColumn: string;
  textColumn: string;
  maxTextLength?: number;
  revisionColumn?: string | null;
  updatedAtColumn?: string | null;
}

const sqlIdentifier = (value: string): string => {
  if (!/^[a-z][a-z0-9_]*$/u.test(value)) {
    throw new Error("Module variable-reference SQL identifiers must be lowercase ASCII identifiers.");
  }
  return `"${value}"`;
};

/** Finds and rewrites channel-variable references in a text column on module-owned rows. */
export const columnVariableReferences = ({
  moduleId,
  tableName,
  idColumn,
  itemNameColumn,
  textColumn,
  maxTextLength = 500,
  revisionColumn = "revision",
  updatedAtColumn = "updated_at",
}: ModuleColumnVariableReferencesOptions): ModuleVariableReferences => {
  const quotedTableName = sqlIdentifier(tableName);
  const quotedIdColumn = sqlIdentifier(idColumn);
  const quotedItemNameColumn = sqlIdentifier(itemNameColumn);
  const quotedTextColumn = sqlIdentifier(textColumn);
  const quotedRevisionColumn = revisionColumn === null ? null : sqlIdentifier(revisionColumn);
  const quotedUpdatedAtColumn = updatedAtColumn === null ? null : sqlIdentifier(updatedAtColumn);

  return {
    async usages(db, channelId, name): Promise<readonly ModuleVariableReferenceUsage[]> {
      const token = `{var.${name}}`;
      const rows = await db.prepare(
        `SELECT ${quotedIdColumn} AS item_id, ${quotedItemNameColumn} AS item_name
           FROM ${quotedTableName}
          WHERE channel_id = ? AND instr(${quotedTextColumn}, ?) > 0
          ORDER BY ${quotedItemNameColumn}, ${quotedIdColumn}`,
      ).bind(channelId, token).all<{ item_id: string; item_name: string }>();
      return rows.results.map((row) => ({
        moduleId,
        itemName: row.item_name || row.item_id,
        kind: "template" as const,
      }));
    },
    async usagesForVariables(db, channelId, names) {
      const uniqueNames = [...new Set(names)];
      const result = new Map<string, ModuleVariableReferenceUsage[]>(uniqueNames.map((name) => [name, []]));
      if (uniqueNames.length === 0) return result;
      const rows = await db.prepare(
        `SELECT requested.value AS variable_name,
                ${quotedIdColumn} AS item_id, ${quotedItemNameColumn} AS item_name
           FROM json_each(?) AS requested
           JOIN ${quotedTableName} AS item
             ON item.channel_id = ? AND instr(item.${quotedTextColumn}, '{var.' || requested.value || '}') > 0
          ORDER BY requested.value, item.${quotedItemNameColumn}, item.${quotedIdColumn}`,
      ).bind(JSON.stringify(uniqueNames), channelId).all<{ variable_name: string; item_id: string; item_name: string }>();
      for (const row of rows.results) {
        result.get(row.variable_name)?.push({
          moduleId,
          itemName: row.item_name || row.item_id,
          kind: "template",
        });
      }
      return result;
    },
    async validateRename(db, channelId, from, to): Promise<ModuleVariableRenameValidation> {
      const oldToken = `{var.${from}}`;
      const nextToken = `{var.${to}}`;
      const rows = await db.prepare(
        `SELECT ${quotedIdColumn} AS item_id, ${quotedItemNameColumn} AS item_name, ${quotedTextColumn} AS template_text
           FROM ${quotedTableName}
          WHERE channel_id = ? AND instr(${quotedTextColumn}, ?) > 0
          ORDER BY ${quotedItemNameColumn}, ${quotedIdColumn}`,
      ).bind(channelId, oldToken).all<{ item_id: string; item_name: string; template_text: string }>();
      const snapshot = rows.results.map((row) => ({ itemId: row.item_id, text: row.template_text }));
      const usages = rows.results.filter((row) =>
        templateTextLength(row.template_text.replaceAll(oldToken, nextToken)) > maxTextLength,
      ).map((row) => ({
        moduleId,
        itemName: row.item_name || row.item_id,
        kind: "template" as const,
      }));
      const snapshotJson = JSON.stringify(snapshot);
      return {
        usages,
        precondition: {
          sql: `NOT EXISTS (
            SELECT 1 FROM (
              SELECT source.${quotedIdColumn} AS item_id, source.${quotedTextColumn} AS template_text
                FROM ${quotedTableName} AS source
               WHERE source."channel_id" = ? AND instr(source.${quotedTextColumn}, ?) > 0
              EXCEPT
              SELECT json_extract(snapshot.value, '$.itemId'), json_extract(snapshot.value, '$.text')
                FROM json_each(?) AS snapshot
            )
          ) AND NOT EXISTS (
            SELECT 1 FROM (
              SELECT json_extract(snapshot.value, '$.itemId'), json_extract(snapshot.value, '$.text')
                FROM json_each(?) AS snapshot
              EXCEPT
              SELECT source.${quotedIdColumn}, source.${quotedTextColumn}
                FROM ${quotedTableName} AS source
               WHERE source."channel_id" = ? AND instr(source.${quotedTextColumn}, ?) > 0
            )
          )`,
          values: [channelId, oldToken, snapshotJson, snapshotJson, channelId, oldToken],
        },
      };
    },
    rename(db, channelId, from, to, guard) {
      const oldToken = `{var.${from}}`;
      const nextToken = `{var.${to}}`;
      const columnUpdates = [
        `${quotedTextColumn} = replace(${quotedTextColumn}, ?, ?)`,
        ...(quotedRevisionColumn === null ? [] : [`${quotedRevisionColumn} = ${quotedRevisionColumn} + 1`]),
        ...(quotedUpdatedAtColumn === null ? [] : [`${quotedUpdatedAtColumn} = strftime('%Y-%m-%dT%H:%M:%fZ', 'now')`]),
      ];
      const target = moduleVariableRenameTarget(channelId, to, guard);
      return [db.prepare(
        `UPDATE ${quotedTableName}
            SET ${columnUpdates.join(", ")}
          WHERE channel_id = ? AND instr(${quotedTextColumn}, ?) > 0
            AND ${target.sql}
            AND NOT EXISTS (SELECT 1 FROM channel_variables WHERE channel_id = ? AND name = ?)`,
      ).bind(oldToken, nextToken, channelId, oldToken, ...target.values, channelId, from)];
    },
  };
};

/** Infrastructure for one-time initial data when a module is enabled. */
export interface ModuleEnableContext {
  DB: D1Database;
  authorizeMutation: AuthorizeModuleMutation;
  /** Prepares a success-coupled audit entry for prepared initial data. */
  prepareModuleAudit?: PrepareModuleAudit;
  actor: ModuleMutationActor;
  now: string;
}

/** Shared props for lazily loaded panel views. */
export interface ModulePanelProperties {
  channelId: string;
  /** Active module navigation page, when a module exposes more than one page. */
  pageId?: string;
  /** The panel language resolved by the host; optional for legacy modules. */
  language?: ModuleLanguage;
  /** Resolves other registered module names for collection rows and history. */
  resolveModuleName?: (moduleId: string, language: ModuleLanguage) => string;
  /** Changes when the channel goes online/offline or a new stream session starts. */
  streamSessionIdentity?: string;
  /** May the view execute management controls? */
  canManage?: boolean;
  /** May the view execute operational controls available to every channel role? */
  canOperate?: boolean;
  /** Active state used by settings surfaces that also mount a module panel. */
  moduleActive?: boolean;
  /** Generic data-source conditions available to text-block editors. */
  textBlockConditions?: readonly ModuleTextBlockConditionDefinition[];
  /** Last known status of the bot's moderator role in this channel. */
  botIsModerator?: boolean | null;
  /** Called by the host when an inspector is closed. */
  onCloseInspector?: () => void;
  /** Deep-link target set by the host (command palette, #164) -- a module reads
   *  its own identifier out of this if it wants to pre-select something on
   *  mount (e.g. text_commands selects the command by name); most modules
   *  ignore it. */
  initialSelection?: string;
  /** Unsaved text-command draft requested by a template shortcut. */
  initialCommand?: { name: string; text: string };
  /** Clears the host-owned initial command request after the editor opens. */
  onInitialCommandConsumed?: () => void;
  /** Opens a saved chat trigger in the dry-run workspace. */
  onOpenTestArea?: (moduleId: "text_commands" | "faq", entryId: string) => void;
}

type ModuleImmediateActionIcon = "ad" | "shoutout" | "clip" | "broadcast" | "vote" | "boss" | "blitz" | "duel" | "quiz" | "skip-forward" | "player-pause" | "player-play" | "square" | "counter" | "test-message" | "marker" | "timer";

type ModuleImmediateActionAvailability = {
  reason: Readonly<Record<ModuleLanguage, string>> | null;
  link?: { href: string; label: Readonly<Record<ModuleLanguage, string>> };
  /** Used for short countdowns such as an open voting round. */
  until?: number;
  /** Snapshot identity that must accompany any later mutation from this action. */
  pollId?: string;
};

interface ModuleImmediateActionConditionContext {
  channelId: string;
  viewerUserId?: string | null;
  streamState: ChannelStreamState | null | undefined;
  botConnected?: boolean | null | undefined;
  language: ModuleLanguage;
  signal: AbortSignal;
}

export type ModuleImmediateActionCondition = "streamLive" | "moduleEnabled" | {
  check: (context: ModuleImmediateActionConditionContext) => Promise<ModuleImmediateActionAvailability>;
} | {
  query: {
    moduleId: string;
    part: string;
    load: (channelId: string, signal: AbortSignal) => Promise<unknown>;
    evaluate: (value: unknown) => ModuleImmediateActionAvailability;
  };
};

export interface ModuleImmediateActionContentProperties {
  channelId: string;
  language: ModuleLanguage;
  onDraftChange?: (dirty: boolean) => void;
  onReadinessChange?: (ready: boolean, reason: string | null) => void;
}

interface ModuleImmediateActionExecutionProperties extends ModuleImmediateActionContentProperties {
  formData: FormData;
  viewerUserId?: string | null;
  pollId?: string;
}

export interface ModuleImmediateActionResult {
  message?: Readonly<Record<ModuleLanguage, string>>;
  tone?: "success" | "info" | "error";
  link?: { href: string; label: Readonly<Record<ModuleLanguage, string>> };
  /** The cockpit drops this feedback once a later availability check confirms the action again (for example after a permission grant). */
  supersededByAvailability?: boolean;
  belaboxSample?: { connected: boolean; bitrateKbps: number; sampledAt: number };
}

export interface ModuleImmediateActionDefinition {
  id: string;
  name: Readonly<Record<ModuleLanguage, string>>;
  icon: ModuleImmediateActionIcon;
  conditions: readonly ModuleImmediateActionCondition[];
  requiredRole: ChannelRole;
  execute: (properties: ModuleImmediateActionExecutionProperties) => Promise<ModuleImmediateActionResult> | Promise<void>;
  errorMessage?: Readonly<Record<ModuleLanguage, string>>;
  content?: {
    presentation: "popover" | "dialog";
    requiresReadiness?: boolean;
    load: () => Promise<{ default: ComponentType<ModuleImmediateActionContentProperties> }>;
  };
}

export type ModuleLiveTileSnapshot =
  | {
    kind: "ad_schedule";
    nextAdAt: string | null;
    duration: number | null;
    snoozeCount: number | null;
    snoozeScopeAvailable: boolean;
    runningBreakEndsAt: string | null;
  }
  | {
    kind: "vote";
    id: string;
    title: string | null;
    options: readonly { label: string; count: number; approved?: boolean; color?: string }[];
    totalVotes: number;
    closesAt: string;
    canExtend: boolean;
  }
  | {
    kind: "votekick";
    id: string;
    target: string;
    endsAt: string;
    yesVotes: number;
    noVotes: number;
    threshold: number;
  }
  | {
    kind: "votekick_recent";
    id: string;
    target: string;
    endedAt: string;
  }
  | {
    kind: "duel";
    id: string;
    challenger: string;
    target: string | null;
    stake: number;
    closesAt: string;
  }
  | {
    kind: "boss_fight";
    id: string;
    currentHp: number;
    maxHp: number;
    attackers: number;
    closesAt: string;
  }
  | {
    kind: "quiz";
    sessionId: string;
    setName: string;
    phase: "announce" | "open" | "reveal" | "results";
    questionIndex: number;
    questionCount: number;
    paused: boolean;
    phaseEndsAt: string | null;
  }
  | {
    kind: "upcoming";
    id: string;
    category: "timer";
    name: string;
    at: string;
    detail: string | null;
  };

interface ModuleLiveTileDefinition {
  load: (channelId: string, signal: AbortSignal) => Promise<readonly ModuleLiveTileSnapshot[]>;
}

export type ModuleLanguage = "de" | "en";

export const MODULE_TEMPLATE_MINIMUM_TIERS = ["everyone", "subscriber", "vip", "moderator", "broadcaster"] as const;
export type ModuleTemplateMinimumTier = (typeof MODULE_TEMPLATE_MINIMUM_TIERS)[number];

/** Chat badges that meet each shared minimum tier. */
export const MODULE_TEMPLATE_TIER_CHAT_STATUSES: Readonly<Record<ModuleTemplateMinimumTier, readonly ModuleChatStatus[]>> = {
  everyone: ["viewer", "subscriber", "vip", "moderator", "broadcaster"],
  subscriber: ["subscriber", "moderator", "broadcaster"],
  vip: ["vip", "moderator", "broadcaster"],
  moderator: ["moderator", "broadcaster"],
  broadcaster: ["broadcaster"],
};

/** Checks a user's Twitch chat badges against the shared minimum-role ladder. */
export const chatStatusMeetsMinimumTier = (
  statuses: readonly ModuleChatStatus[] | null,
  minimumTier: ModuleTemplateMinimumTier | "viewer",
): boolean => minimumTier === "viewer" || minimumTier === "everyone" ||
  statuses?.some((status) => MODULE_TEMPLATE_TIER_CHAT_STATUSES[minimumTier].includes(status)) === true;

/** A variable name provided by a module for template validation. */
export interface ModuleRegisteredTemplateVariable extends TemplateVariable {
  moduleId: string;
  isTextBlock: boolean;
  /** Whether a generic event template renderer can resolve this module variable. */
  availableInEventTemplates?: boolean;
  description?: string;
  pickerGroup?: ModuleTemplateVariableGroup & { id: string };
}

export const browserModuleLanguage = (): ModuleLanguage => {
  const language = typeof navigator === "undefined" ? "de" : navigator.language;
  return language.toLowerCase().startsWith("de") ? "de" : "en";
};

/** HTTP method a Helix request may use; `helixRequest` sets no default body encoding beyond JSON. */
type HelixMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Base transport-level classification `helixRequest` derives from the raw
 * response -- nothing about what a status *means* for a given endpoint (a
 * 401 is a missing scope here, a revoked moderator there). That reading
 * stays with the caller; see `worker/twitch/helix.ts`'s header comment.
 */
export type HelixErrorReason =
  | "rate_limited" | "network_error" | "timeout" | "invalid_response" | "pagination_loop"
  | `http_${string}`;

export interface HelixRequestOptions<Data = unknown> {
  method?: HelixMethod;
  url: string;
  query?: Readonly<Record<string, string | undefined>>;
  body?: unknown;
  accessToken: string;
  clientId: string;
  /** Validates and narrows a successful response body; a mismatch reports as `invalid_response`. */
  schema?: z.ZodType<Data>;
  fetcher?: typeof fetch;
  timeoutMs?: number;
}

export type HelixResult<Data = unknown> =
  | { ok: true; status: number; data: Data }
  | {
    ok: false;
    status: number | null;
    reason: HelixErrorReason;
    message: string | null;
    /** Twitch's parsed error body, tolerant of a missing or non-JSON response. */
    body: Readonly<Record<string, unknown>>;
    /** Absolute Unix timestamp in milliseconds from Twitch's rate-limit headers. */
    retryAfter?: number | null;
    /** Remaining points in the general API bucket, when Twitch provides the header. */
    rateLimitRemaining?: number | null;
  };

export type HelixRequest = <Data = unknown>(options: HelixRequestOptions<Data>) => Promise<HelixResult<Data>>;

export interface ModuleTwitchEmote {
  id: string;
  name: string;
  images?: { url_1x?: string; url_2x?: string; url_4x?: string };
}

export type ModuleTwitchEmoteList = (options: {
  clientId: string;
  accessToken: string;
  userId?: string;
  broadcasterId?: string;
}) => Promise<HelixResult<ModuleTwitchEmote[]>>;

export interface ModuleRouteVariables {
  session: { userId: string; sessionId: string; login?: string };
  channelRole: ChannelRole;
  actor: { userId: string; sessionId: string };
  /** Reads the saved location only for the authorized channel route. */
  channelLocation: (channelId: string) => Promise<ModuleChannelLocation | null>;
  authorizeMutation: AuthorizeModuleMutation;
  authorizeManagementMutation: AuthorizeModuleMutation;
  /** Returns secret access bound to the authorized route channel and mounted module. */
  secrets: (channelId: string) => ModuleSecretAccess;
  externalFetchBudget: ModuleExternalFetchBudget;
  /** Returns ballot access bound to the authorized route channel and mounted module. */
  ballots: (channelId: string) => ModuleBallotAccess;
  /** Returns point ledger access bound to the authorized route channel and mounted module. */
  points: (channelId: string) => ModulePointsAccess;
  /** Returns temporary VIP access bound to the authorized route channel and module. */
  tempVips: (channelId: string) => ModuleTempVipRouteAccess;
  /** Returns the bot token and granted scopes for the authorized channel when available. */
  botCredentials?: (channelId: string) => Promise<{ userId: string; accessToken: string; scopes: readonly string[] } | null>;
  /** Reads blocked terms only for the authorized route channel. */
  readChannelBlockedTerms?: (channelId: string) => Promise<readonly string[] | null>;
  /** Runs a registered module alarm immediately for a route that must reconcile module-owned state. */
  runModuleAlarm: (
    channelId: string,
    moduleId: string,
    handlerKey: string,
    alarmKey: string,
    invocation?: unknown,
  ) => Promise<unknown>;
  prepareModuleAudit: PrepareModuleAudit;
  writeModuleAudit: WriteModuleAudit;
  listChannelVariables: ModuleChannelVariableAccess["listChannelVariables"];
  findChannelVariable: ModuleChannelVariableAccess["findChannelVariable"];
  templateUsageSources: (channelId: string) => Promise<readonly ModuleTemplateUsageSource[]>;
  listRegisteredTemplateVariables: (channelId: string) => Promise<readonly ModuleRegisteredTemplateVariable[]>;
  listTextBlockConditions: (channelId: string) => Promise<readonly ModuleTextBlockConditionDefinition[]>;
  listEventTimeSources: () => readonly { id: string; label: Readonly<Record<ModuleLanguage, string>> }[];
  resolveEventTimes: (channelId: string, now: number) => Promise<readonly ResolvedModuleEventTime[]>;
  notifyScheduleInputsChanged: (channelId: string, reason: ModuleScheduleInputChangeReason) => Promise<void>;
  /** Refreshes timer config snapshots after host-managed template rewrites. */
  reconcileTimerSchedules: (channelId: string) => Promise<void>;
  validateTemplateContentMutation: (
    channelId: string,
    candidate: ModuleTemplateContentCandidate,
  ) => Promise<readonly ModuleTemplateContentIssue[]>;
  /** Notifies modules after a text block rename rewrites their stored templates. */
  afterTextBlockRename: (channelId: string) => void;
  resolveTextBlockConditions: (
    channelId: string,
    ids: readonly string[],
    now: number,
    options?: { readOnly?: boolean },
  ) => Promise<Readonly<Record<string, string>>>;
  publishModuleOverlayMessage: (
    channelId: string,
    moduleId: string,
    type: string,
    elementKind: string,
    payload: Readonly<Record<string, unknown>>,
    recipientConfig?: { field: string; value: string },
  ) => Promise<void>;
  publishOverlayHostEvent: (channelId: string, event: ModuleOverlayHostEvent) => Promise<void>;
  writeModuleDiagnostics: (
    db: D1Database,
    channelId: string,
    moduleId: string,
    triggerId: string,
    actorUserId: string | null,
    diagnostics: readonly ModuleDiagnostic[],
    now: string,
  ) => Promise<unknown>;
  broadcasterHasScope: (db: D1Database, channelId: string, scope: string) => Promise<boolean>;
  broadcasterScopesForChannel: (db: D1Database, channelId: string) => Promise<readonly string[]>;
  measureServerTiming: <T>(phase: "auth" | "d1" | "do" | "helix", run: () => Promise<T>) => Promise<T>;
  recordServerTiming: (phase: "auth" | "d1" | "do" | "helix", durationMs: number) => void;
  scheduleBackgroundWork: (work: Promise<unknown>) => void;
  getAppAccessToken: (environment: Env, now: string, fetcher?: typeof fetch, forceRefresh?: boolean) => Promise<string>;
  /** Thin Helix HTTP transport (issue #163); modules never talk to `api.twitch.tv` directly. */
  helixRequest: HelixRequest;
  getUserEmotes: ModuleTwitchEmoteList;
  getGlobalEmotes: ModuleTwitchEmoteList;
  liftModerationBan: (channelId: string, userId: string, expected: ModerationTimeoutExpectation) => Promise<ModerationResult>;
}

export interface ModuleRouteEnvironment {
  Bindings: Env;
  Variables: ModuleRouteVariables;
}

/**
 * What a module learns about the triggering event. Deliberately narrow: the
 * channel comes from the verified event, not from the module, so a module
 * cannot act on a foreign channel. `triggerId` is Twitch's message ID and
 * links all rows in the event log that belong to the same trigger.
 */
export interface ModuleEvent<Settings = unknown> {
  channelId: string;
  subscriptionType: string;
  /** The variant, like the channel, comes from the EventSub subscription condition. */
  subscriptionVariant?: string;
  triggerId: string;
  payload: Readonly<Record<string, unknown>>;
  settings: Settings;
  receivedAt: string;
  /** Verified EventSub message time from the host, when available. */
  eventSubTimestamp?: string;
  /** The host already applied points account, activity, and safety-net chat bookkeeping. */
  pointChatPrepared?: { welcomePending: boolean; pendingWelcome: StoredPointWelcome | null };
  /** The role comes from channel_members; `null` means not a member. */
  actor: ModuleActor | null;
  /** Events unrelated to chat carry `null` here; chat events carry all matching statuses. */
  chatStatus: readonly ModuleChatStatus[] | null;
}

export type BotModule<SettingsSchema extends z.ZodType = z.ZodType> = {
  id: string;
  /** Host-catalog category used to place this module in the channel sidebar. */
  navigationCategory: ModuleNavigationCategory;
  panelIcon?: ModuleIconDescriptor;
  /** Other optional modules that must already be enabled for this module to activate. */
  requiresModules?: readonly string[];
  /** Read-only help shown on the module page for commands listened to in chat. */
  chatCommands?: readonly ModuleChatCommandDeclaration[];
  /** Effective command tokens for settings-configurable command modules. */
  chatCommandNames?: (settings: unknown) => readonly string[];
  /** Root settings fields that store the configurable command tokens for SQL write guards. */
  chatCommandSettingsFields?: readonly string[];
  /** Command tokens inserted by onEnable and included in activation collision guards. */
  onEnableCommandTokens?: readonly string[];
  /** The module is always enabled for every released channel and cannot be disabled. */
  mandatory?: boolean;
  /** Localized explanation shown when this module cannot be disabled. */
  mandatoryReason?: Readonly<Record<ModuleLanguage, string>>;
  /**
   * The declaration the release path uses to write an enabled
   * `channel_modules` row for new channels. A channel released before the
   * module existed gets that row from a backfill migration instead. Every
   * reader treats a missing row as disabled — this flag never substitutes
   * for the row at read time.
   */
  defaultEnabled?: boolean;
  /** Tracks per-user chat activity for the current stream while this module is enabled. */
  needsActiveChatters?: boolean;
  settingsSchema: SettingsSchema;
  /** Lenient validator for persisted settings written before current message rules existed. */
  settingsReadSchema?: z.ZodType<z.output<SettingsSchema>>;
  defaultSettings: z.output<SettingsSchema>;
  /** Optional channel-language defaults for modules whose initial messages are localized. */
  defaultSettingsForLanguage?: (language: ModuleLanguage) => z.output<SettingsSchema>;
  /** Template fields and variables used by both panel validation and worker rendering. */
  templateFields?: TemplateFields<z.output<SettingsSchema>>;
  /** Static declarations for variables exposed through a dynamic provider. */
  templateVariableCatalog?: readonly TemplateVariable[];
  /** Localized source group and icon for variables provided by this module. */
  templateVariableGroup?: ModuleTemplateVariableGroup;
  /** Dynamic template variables registered from module-owned data. */
  templateVariables?: (db: D1Database, channelId: string) => Promise<readonly TemplateVariable[]>;
  /** Which host catalog groups are available in this module's templates. */
  templateContext?: TemplateContext;
  /** Requests syntactically valid names even when they are absent from this provider's declarations. */
  templateVariableNamespace?: "text_blocks" | "api_sources";
  /** Resolves only the declared names requested from the original template fragment. */
  resolveTemplateValues?: (
    names: readonly string[],
    context: ModuleTemplateValueContext,
  ) => Promise<Readonly<Record<string, string>>>;
  /** Resolves a declared parameterized value; parameters are never recursively rendered. */
  resolveTemplateParameter?: (
    name: string,
    parameter: string,
    context: ModuleTemplateValueContext,
  ) => Promise<string | null>;
  /** Bilingual generic fallback if this provider cannot resolve a declared value. */
  templateUnavailableText?: Readonly<Record<ModuleLanguage, string>>;
  /** Declares generic conditions that module-owned text blocks can select. */
  textBlockConditions?: readonly ModuleTextBlockConditionDefinition[];
  /** Dynamic condition IDs accepted without loading a channel catalog up front. */
  textBlockConditionNamespace?: string;
  /** Channel-specific condition choices backed by module-owned data. */
  textBlockConditionsForChannel?: (
    db: D1Database,
    channelId: string,
  ) => Promise<readonly ModuleTextBlockConditionDefinition[]>;
  /** Resolves condition ids declared by this module for a single template render. */
  resolveTemplateConditions?: (
    ids: readonly string[],
    context: ModuleTemplateConditionContext,
  ) => Promise<Readonly<Record<string, string>>>;
  /** Returns value changes for time-dependent text block conditions over an interval. */
  resolveTemplateConditionTransitions?: (
    ids: readonly string[],
    context: ModuleTemplateConditionTimelineContext,
  ) => Promise<readonly ModuleTemplateConditionTransition[]>;
  /** Declares values the overlay browser formats from target instants. */
  dynamicTemplateVariableNames?: readonly string[];
  /** Resolves overlay-only availability and target instants without changing template state. */
  resolveOverlayTemplateValues?: (
    names: readonly string[],
    context: {
      DB: D1Database;
      channelId: string;
      now: number;
      channelTimeZone: () => Promise<string>;
      channelLocation: () => Promise<ModuleChannelLocation | null>;
      language: ModuleLanguage;
    },
  ) => Promise<Readonly<Record<string, ModuleOverlayTemplateValue>>>;
  /** Channel navigation entries contributed by this module. */
  navigationEntries?: readonly ModuleNavigationEntry[];
  /** Template text contributed by this module for generic library usage views. */
  templateUsageSources?: (
    db: D1Database,
    channelId: string,
  ) => Promise<readonly ModuleTemplateUsageSource[]>;
  /** Validates edits to shared template content against this module's consumers. */
  validateTemplateContent?: (
    context: ModuleTemplateContentValidationContext,
  ) => Promise<readonly ModuleTemplateContentIssue[]>;
  /** Clears module-owned caches after a shared text block rename updates stored templates. */
  afterTextBlockRename?: (db: D1Database, channelId: string) => void;
  /** Optional references to channel variables stored in module-owned data. */
  variableReferences?: ModuleVariableReferences;
  /** Broadcaster consent the host verifies before the EventSub subscription. */
  broadcasterScopes?: readonly string[];
  eventSubTypes?: readonly string[];
  /** EventSub types authenticated by the bot token and independent of broadcaster scopes. */
  scopeIndependentEventSubTypes?: readonly string[];
  /** Lifecycle inputs this optional module must still receive while channel automation is paused. */
  pauseSafeEventSubTypes?: readonly string[];
  routes?: Hono<ModuleRouteEnvironment>;
  /** Overlay presentation declarations, with view and editor chunks loaded on demand. */
  overlayElements?: readonly ModuleOverlayElementDefinition[];
  /** Realtime element kinds a later overlay slice will implement without registering an element UI yet. */
  overlayRealtimeElementKinds?: readonly string[];
  /** Durable alarm handlers registered through the shared host contract. */
  alarms?: readonly ModuleAlarmDefinition[];
  /** Serializes this module's alarm and point-round lifecycle mutations per channel. */
  serializeLifecycleMutations?: boolean;
  /** Serializes point-round deadline callbacks and defers resolution notifications to recovery alarms. */
  serializePointRoundDeadline?: boolean;
  /** Reconciles accepted start metadata, even if the round was cancelled before announcement. */
  onPointRoundOpened?: (context: ModuleAlarmContext, round: ModulePointRoundAnnouncement) => Promise<void>;
  /** Runs after the shared start announcement has opened the round and before its module-owned phase begins. */
  onPointRoundAnnounced?: (context: ModuleAlarmContext, round: ModulePointRoundAnnouncement) => Promise<void>;
  /** Receives an internal, replayable notification immediately after round resolution and during watchdog recovery. */
  onPointRoundResolved?: (context: ModuleAlarmContext, round: ModulePointRoundResult) => Promise<void>;
  /** Handles a shared round deadline when a module owns multiple phases inside one points round. */
  onPointRoundDeadline?: (context: ModuleAlarmContext, round: ModulePointRoundDeadline) => Promise<void>;
  /** Preserves a module-owned deadline when its post-announcement lifecycle hook fails. */
  preservePointRoundDeadlineOnAnnouncementFailure?: boolean;
  /** Retries channel-control-suppressed start announcements without consuming the attempt limit. */
  retrySuppressedPointRoundAnnouncements?: boolean;
  /** Whether cancelling funded rounds queues the shared refund notice. Defaults to true. */
  pointRoundCancellationNotice?: boolean;
  /** Formats the persisted result of a resolved points round for retryable chat delivery. */
  formatPointRoundResult?: (
    context: ModuleAlarmContext,
    round: ModulePointRoundResult,
  ) => Promise<{
    text: string;
    attributions?: readonly string[];
    target?: ChatOutputTarget;
    replyToMessageId?: string;
    sourceBroadcasterUserId?: string;
    automated?: boolean;
  } | null>;
  formatPointRoundAnnouncement?: (
    context: ModuleAlarmContext,
    round: ModulePointRoundAnnouncement,
  ) => Promise<{
    kind?: "message";
    text: string;
    attributions?: readonly string[];
    target?: ChatOutputTarget;
    replyToMessageId?: string;
    sourceBroadcasterUserId?: string;
    automated?: boolean;
  } | { kind: "silent" } | null>;
  /** Retries this module's result after shared automated-output limit rejections until delivered. */
  retryPointRoundRateLimitedResults?: boolean;
  /** Hourly module-owned cleanup run by the host through the registry. */
  scheduledMaintenance?: (db: D1Database, now: string) => Promise<void>;
  /** Generic event-time sources that scheduled modules may select. */
  eventTimeSources?: readonly ModuleEventTimeSource[];
  /**
   * The business entry point. A pure function: it describes what should
   * happen and executes nothing. The host executes the actions and logs
   * their outcome; the module justifies with `diagnostics` why it acted or
   * did not act (decision 0004).
   *
   * If the function throws, this blocks neither the worker nor the other
   * modules — the error ends up as a diagnostic in the event log.
   */
  handleEvent?: (
    event: ModuleEvent<z.output<SettingsSchema>>,
    context: ModuleExecutionContext,
  ) => ModuleResult | Promise<ModuleResult>;
  /** Explains why a chat subscription has no single-message reaction. */
  dryRunSilentReason?: "activity_tracking" | "instant_settlement" | "round_answers";
  /** Reuses the production service with read-only adapters and describes its effects without executing them. */
  dryRunChatMessage?: (
    event: ModuleEvent<z.output<SettingsSchema>>,
    context: ModuleDryRunContext,
  ) => Promise<readonly DryRunEntryReport[]>;
  /** Called on first enable to create module-specific initial data. */
  onEnable?: (
    context: ModuleEnableContext,
    channelId: string,
  ) => readonly D1PreparedStatement[] | undefined | Promise<readonly D1PreparedStatement[] | undefined>;
  // Module migrations and further action kinds join the contract once the
  // first module needs them. Command processing launched with
  // ModuleResult.
  /**
   * This view also stays deliberately lazy: a disabled module should
   * likewise cost zero bytes in the panel bundle. Do not turn this into a
   * direct import.
   */
  panel?: () => Promise<{ default: ComponentType<ModulePanelProperties> }>;
  /** The custom panel owns this module's settings form and save lifecycle. */
  panelManagesSettings?: boolean;
  /** Lazily loaded editor declaration for this module's settings. */
  settingsEditor?: () => Promise<{ default: SettingsEditorDefinition<z.output<SettingsSchema>> }>;
  /** Module query parts that depend on settings edited by this editor. */
  settingsEditorRelatedParts?: readonly string[];
  /** Places the settings editor before a potentially long module panel. */
  settingsEditorPlacement?: "before-panel" | "after-panel" | "page";
  /** Server-side semantic validation after the shared settings schema parses. */
  validateSettings?: (settings: z.output<SettingsSchema>) => string | null;
  /** Reconciles one module-owned alarm immediately after settings are saved. */
  settingsChangedAlarm?: { handlerKey: string; alarmKey: string };
  /** Individual dashboard actions; only their optional content is loaded on demand. */
  actions?: readonly ModuleImmediateActionDefinition[];
  /** Read-only start and participation guidance for game pages. */
  howToPlay?: ModuleHowToPlayDefinition;
  /** Optional live state read by the host; the host owns its fixed-size tile presentation. */
  liveTile?: ModuleLiveTileDefinition;
};

/**
 * A localized hint about one argument accepted by a built-in chat command.
 * The name is shown in the command's syntax, and the hint explains it in the
 * dashboard language.
 */
interface ModuleChatCommandArgument {
  name: string;
  nameByLanguage?: Readonly<Record<ModuleLanguage, string>>;
  hint: Readonly<Record<ModuleLanguage, string>>;
}

export type ModuleChatCommandThreshold = "everyone" | Exclude<ModuleChatStatus, "viewer"> | "configurable";

/**
 * Help metadata for a command a module parses from Twitch chat. This is
 * descriptive only: the module remains responsible for parsing and access
 * checks in its domain and service code.
 */
export interface ModuleChatCommandDeclaration {
  /** Command token consumed by the module parser, such as `!vote`. */
  name: string;
  /** A representative form users can type, such as `!votekick @user`. */
  syntax: string;
  /** Localized forms for commands whose argument names are shown in the dashboard. */
  syntaxByLanguage?: Readonly<Record<ModuleLanguage, string>>;
  description: Readonly<Record<ModuleLanguage, string>>;
  arguments?: readonly ModuleChatCommandArgument[];
  /** Minimum Twitch chat badge/status required; configurable means module settings determine it. */
  minimumChatStatus: ModuleChatCommandThreshold;
  /** Settings field that selects the effective minimum status when the threshold is configurable. */
  minimumChatStatusSetting?: string;
  /** How this command relates to starting and joining the game. */
  howToPlayUsage?: "start" | "participate" | "start_and_participate";
}
